import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Popover } from 'antd';
import {
  MenuOutlined,
  SearchOutlined,
  BulbOutlined,
  ReadOutlined,
  SoundOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { loadBibleData, loadBibleMetadata, loadBibleBook, getAvailableVersions, clearBibleCache } from './services/bible';
import { resolveBibleDefaults } from './services/bibleLocaleDefaults';
import { db } from './db/state';
import { useValue } from './lib/db/react';
import './app.css';

const debounce = <T extends (...args: any[]) => any>(func: T, delay: number) => {
    let timeoutId: number | null = null;
    return (...args: Parameters<T>) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => func(...args), delay);
    };
  };

type UiText = {
  loading: string;
  error: string;
  searchPlaceholder: string;
  cancel: string;
  verses: string;
  footnotes: string;
  allBooks: string;
  noSearchResult: string;
  searchHint: string;
  noBookResult: string;
  toc: string;
  bookmarks: string;
  notes: string;
  chooseBook: string;
  oldTestament: string;
  newTestament: string;
  topic: string;
  themeLight: string;
  themeDark: string;
  themeClassic: string;
  titleChapterPrefix: string;
  titleChapterSuffix: string;
  chapterLabel: string;
  verseLabel: string;
  versionPicker: string;
  clearCache: string;
  clearingCache: string;
  version: string;
  search: string;
  theme: string;
  readAloud: string;
  stopReading: string;
  noOutline: string;
  noVerseFound: string;
  noFootnoteFound: string;
  mergedVerseNote: string;
  versionFeedback: string;
  copyVerse: string;
  highlightVerse: string;
  unhighlightVerse: string;
  imageGen: string;
};

const EN_UI_TEXT: UiText = {
  loading: 'Loading...',
  error: 'Error',
  searchPlaceholder: 'Search verses and notes',
  cancel: 'Cancel',
  verses: 'Verses',
  footnotes: 'Notes',
  allBooks: 'All books',
  noSearchResult: 'No matches found',
  searchHint: 'Type to search verses',
  noBookResult: 'No matches in this book',
  toc: 'Contents',
  bookmarks: 'Bookmarks',
  notes: 'Notes',
  chooseBook: 'Please select a book',
  oldTestament: 'OT',
  newTestament: 'NT',
  topic: 'Topic',
  themeLight: 'Light',
  themeDark: 'Dark',
  themeClassic: 'Classic',
  titleChapterPrefix: 'Chapter ',
  titleChapterSuffix: '',
  chapterLabel: 'Chapter',
  verseLabel: 'Verse',
  versionPicker: 'Choose versions',
  clearCache: 'Clear cache',
  clearingCache: 'Clearing...',
  version: 'Versions',
  search: 'Search',
  theme: 'Theme',
  readAloud: 'Play audio',
  stopReading: 'Stop audio',
  noOutline: 'No outline data',
  noVerseFound: 'Verse not found',
  noFootnoteFound: 'Annotation not found',
  mergedVerseNote: ' merged with prev.',
  versionFeedback: 'Data issues / More versions? Contact: privacy@aliving.stream',
  copyVerse: 'Copy verse',
  highlightVerse: 'Highlight',
  unhighlightVerse: 'Unhighlight',
  imageGen: 'Image',
};

const UI_TEXT: Record<string, typeof EN_UI_TEXT> = {
  'en': EN_UI_TEXT,
  'zh': {
    ...EN_UI_TEXT,
    loading: '加载中...',
    error: '错误',
    searchPlaceholder: '搜索经文和注解',
    cancel: '取消',
    verses: '经文',
    footnotes: '注解',
    allBooks: '所有书卷',
    noSearchResult: '未找到匹配内容',
    searchHint: '输入关键词搜索经文',
    noBookResult: '该书卷无匹配结果',
    toc: '目录',
    bookmarks: '书签',
    notes: '笔记',
    chooseBook: '请选择一卷书',
    oldTestament: '旧约',
    newTestament: '新约',
    topic: '主题',
    themeLight: '明亮',
    themeDark: '夜间',
    themeClassic: '典雅',
    titleChapterPrefix: '第',
    titleChapterSuffix: '章',
    chapterLabel: '章',
    verseLabel: '节',
    versionPicker: '选择圣经版本',
    clearCache: '清除缓存',
    clearingCache: '清除中...',
    version: '版本',
    search: '搜索',
    theme: '主题',
    readAloud: '语音播报',
    stopReading: '停止播报',
    noOutline: '暂无纲目数据',
    noVerseFound: '未找到相关经文',
    noFootnoteFound: '未找到相关注解',
    mergedVerseNote: '节合并。',
    versionFeedback: '数据问题 / 更多版本？联系：privacy@aliving.stream',
    copyVerse: '复制经文',
    highlightVerse: '重点标注',
    unhighlightVerse: '取消标注',
    imageGen: '美图生成',
  },
  'zh-hant': {
    ...EN_UI_TEXT,
    loading: '載入中...',
    error: '錯誤',
    searchPlaceholder: '搜尋經文和註解',
    cancel: '取消',
    verses: '經文',
    footnotes: '註解',
    allBooks: '所有書卷',
    noSearchResult: '未找到匹配內容',
    searchHint: '輸入關鍵詞搜尋經文',
    noBookResult: '該書卷無匹配結果',
    toc: '目錄',
    bookmarks: '書籤',
    notes: '筆記',
    chooseBook: '請選擇一卷書',
    oldTestament: '舊約',
    newTestament: '新約',
    topic: '主題',
    themeLight: '明亮',
    themeDark: '夜間',
    themeClassic: '典雅',
    titleChapterPrefix: '第',
    titleChapterSuffix: '章',
    chapterLabel: '章',
    verseLabel: '節',
    versionPicker: '選擇聖經版本',
    clearCache: '清除快取',
    clearingCache: '清除中...',
    version: '版本',
    search: '搜尋',
    theme: '主題',
    readAloud: '語音播報',
    stopReading: '停止播報',
    noOutline: '暫無綱目資料',
    noVerseFound: '未找到相關經文',
    noFootnoteFound: '未找到相關註解',
    mergedVerseNote: '節合併。',
    versionFeedback: '資料問題 / 更多版本？聯絡：privacy@aliving.stream',
    copyVerse: '複製經文',
    highlightVerse: '重點標註',
    unhighlightVerse: '取消標註',
    imageGen: '美圖生成',
  },
  'cs': {
    ...EN_UI_TEXT,
    loading: 'Načítání...',
    error: 'Chyba',
    searchPlaceholder: 'Hledat verše',
    verses: 'Verše',
    footnotes: 'Poznámky',
    allBooks: 'Všechny knihy',
    noSearchResult: 'Nebyly nalezeny žádné výsledky',
    searchHint: 'Zadejte text pro hledání veršů',
    noBookResult: 'V této knize nic nenalezeno',
    toc: 'Obsah',
    bookmarks: 'Záložky',
    notes: 'Poznámky',
    chooseBook: 'Vyberte knihu',
    oldTestament: 'SZ',
    newTestament: 'NZ',
    topic: 'Téma',
    themeLight: 'Světlé',
    themeDark: 'Tmavé',
    themeClassic: 'Klasické',
    titleChapterPrefix: 'Kapitola ',
    titleChapterSuffix: '',
    versionPicker: 'Vyberte verze',
    version: 'Verze',
    search: 'Hledat',
    theme: 'Motiv',
    noOutline: 'Data osnovy nejsou k dispozici',
    noVerseFound: 'Verš nebyl nalezen',
    noFootnoteFound: 'Poznámka nebyla nalezena',
    cancel: 'Zrušit',
    chapterLabel: 'Kapitola',
    verseLabel: 'Verš',
    clearCache: 'Vymazat mezipaměť',
    clearingCache: 'Mazání...',
    mergedVerseNote: ' sloučeno s předchozím.',
    copyVerse: 'Kopírovat verš',
    highlightVerse: 'Zvýraznit',
    unhighlightVerse: 'Zrušit zvýraznění',
    imageGen: 'Obrázek',
  },
  'da': {
    ...EN_UI_TEXT,
    loading: 'Indlæser...',
    error: 'Fejl',
    searchPlaceholder: 'Søg i vers',
    verses: 'Vers',
    footnotes: 'Noter',
    allBooks: 'Alle bøger',
    noSearchResult: 'Ingen resultater fundet',
    searchHint: 'Skriv for at søge i vers',
    noBookResult: 'Ingen resultater i denne bog',
    toc: 'Indhold',
    bookmarks: 'Bogmærker',
    notes: 'Noter',
    chooseBook: 'Vælg en bog',
    oldTestament: 'GT',
    newTestament: 'NT',
    topic: 'Tema',
    themeLight: 'Lys',
    themeDark: 'Mørk',
    themeClassic: 'Klassisk',
    titleChapterPrefix: 'Kapitel ',
    titleChapterSuffix: '',
    versionPicker: 'Vælg versioner',
    version: 'Versioner',
    search: 'Søg',
    theme: 'Tema',
    noOutline: 'Ingen dispositionsdata',
    noVerseFound: 'Vers ikke fundet',
    noFootnoteFound: 'Note ikke fundet',
    cancel: 'Annuller',
    chapterLabel: 'Kapitel',
    verseLabel: 'Vers',
    clearCache: 'Ryd cache',
    clearingCache: 'Rydder...',
    mergedVerseNote: ' flettet med forrige.',
    copyVerse: 'Kopiér vers',
    highlightVerse: 'Fremhæv',
    unhighlightVerse: 'Fjern fremhævning',
    imageGen: 'Billede',
  },
  'de': {
    ...EN_UI_TEXT,
    loading: 'Wird geladen...',
    error: 'Fehler',
    searchPlaceholder: 'Verse suchen',
    verses: 'Verse',
    footnotes: 'Anmerkungen',
    allBooks: 'Alle Bücher',
    noSearchResult: 'Keine Treffer gefunden',
    searchHint: 'Zum Suchen tippen',
    noBookResult: 'Keine Treffer in diesem Buch',
    toc: 'Inhalt',
    bookmarks: 'Lesezeichen',
    notes: 'Notizen',
    chooseBook: 'Bitte ein Buch wählen',
    oldTestament: 'AT',
    newTestament: 'NT',
    topic: 'Thema',
    themeLight: 'Hell',
    themeDark: 'Dunkel',
    themeClassic: 'Klassisch',
    titleChapterPrefix: 'Kapitel ',
    titleChapterSuffix: '',
    versionPicker: 'Versionen wählen',
    version: 'Versionen',
    search: 'Suchen',
    theme: 'Thema',
    noOutline: 'Keine Gliederungsdaten',
    noVerseFound: 'Vers nicht gefunden',
    noFootnoteFound: 'Anmerkung nicht gefunden',
    cancel: 'Abbrechen',
    chapterLabel: 'Kapitel',
    verseLabel: 'Vers',
    clearCache: 'Cache leeren',
    clearingCache: 'Wird geleert...',
    mergedVerseNote: ' mit vorherigem zusammengeführt.',
    copyVerse: 'Vers kopieren',
    highlightVerse: 'Hervorheben',
    unhighlightVerse: 'Hervorhebung entfernen',
    imageGen: 'Bild',
  },
  'el': {
    ...EN_UI_TEXT,
    loading: 'Φόρτωση...',
    error: 'Σφάλμα',
    searchPlaceholder: 'Αναζήτηση εδαφίων',
    verses: 'Εδάφια',
    footnotes: 'Σημειώσεις',
    allBooks: 'Όλα τα βιβλία',
    noSearchResult: 'Δεν βρέθηκαν αποτελέσματα',
    searchHint: 'Πληκτρολογήστε για αναζήτηση εδαφίων',
    noBookResult: 'Δεν βρέθηκαν αποτελέσματα σε αυτό το βιβλίο',
    toc: 'Περιεχόμενα',
    bookmarks: 'Σελιδοδείκτες',
    notes: 'Σημειώσεις',
    chooseBook: 'Επιλέξτε βιβλίο',
    oldTestament: 'ΠΔ',
    newTestament: 'ΚΔ',
    topic: 'Θέμα',
    themeLight: 'Φωτεινό',
    themeDark: 'Σκοτεινό',
    themeClassic: 'Κλασικό',
    titleChapterPrefix: 'Κεφάλαιο ',
    titleChapterSuffix: '',
    versionPicker: 'Επιλογή εκδόσεων',
    version: 'Εκδόσεις',
    search: 'Αναζήτηση',
    theme: 'Θέμα',
    noOutline: 'Δεν υπάρχουν δεδομένα διαγράμματος',
    noVerseFound: 'Δεν βρέθηκε εδάφιο',
    noFootnoteFound: 'Δεν βρέθηκε σημείωση',
    cancel: 'Ακύρωση',
    chapterLabel: 'Κεφάλαιο',
    verseLabel: 'Εδάφιο',
    clearCache: 'Εκκαθάριση προσωρινής μνήμης',
    clearingCache: 'Εκκαθάριση...',
    mergedVerseNote: ' συγχωνεύτηκε με το προηγούμενο.',
    copyVerse: 'Αντιγραφή εδαφίου',
    highlightVerse: 'Επισήμανση',
    unhighlightVerse: 'Ακύρωση επισήμανσης',
    imageGen: 'Εικόνα',
  },
  'es': {
    ...EN_UI_TEXT,
    loading: 'Cargando...',
    error: 'Error',
    searchPlaceholder: 'Buscar versículos',
    verses: 'Versículos',
    footnotes: 'Notas',
    allBooks: 'Todos los libros',
    noSearchResult: 'No se encontraron resultados',
    searchHint: 'Escriba para buscar versículos',
    noBookResult: 'No hay resultados en este libro',
    toc: 'Contenido',
    bookmarks: 'Marcadores',
    notes: 'Notas',
    chooseBook: 'Seleccione un libro',
    oldTestament: 'AT',
    newTestament: 'NT',
    topic: 'Tema',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    themeClassic: 'Clásico',
    titleChapterPrefix: 'Capítulo ',
    titleChapterSuffix: '',
    versionPicker: 'Elegir versiones',
    version: 'Versiones',
    search: 'Buscar',
    theme: 'Tema',
    noOutline: 'No hay datos de esquema',
    noVerseFound: 'Versículo no encontrado',
    noFootnoteFound: 'Anotación no encontrada',
    cancel: 'Cancelar',
    chapterLabel: 'Capítulo',
    verseLabel: 'Versículo',
    clearCache: 'Borrar caché',
    clearingCache: 'Borrando...',
    mergedVerseNote: ' combinado con el anterior.',
    copyVerse: 'Copiar versículo',
    highlightVerse: 'Destacar',
    unhighlightVerse: 'Quitar destacado',
    imageGen: 'Imagen',
  },
  'fi': {
    ...EN_UI_TEXT,
    loading: 'Ladataan...',
    error: 'Virhe',
    searchPlaceholder: 'Hae jakeita',
    verses: 'Jakeet',
    footnotes: 'Huomautukset',
    allBooks: 'Kaikki kirjat',
    noSearchResult: 'Ei hakutuloksia',
    searchHint: 'Kirjoita hakeaksesi jakeita',
    noBookResult: 'Ei tuloksia tässä kirjassa',
    toc: 'Sisällys',
    bookmarks: 'Kirjanmerkit',
    notes: 'Muistiinpanot',
    chooseBook: 'Valitse kirja',
    oldTestament: 'VT',
    newTestament: 'UT',
    topic: 'Aihe',
    themeLight: 'Vaalea',
    themeDark: 'Tumma',
    themeClassic: 'Klassinen',
    titleChapterPrefix: 'Luku ',
    titleChapterSuffix: '',
    versionPicker: 'Valitse versiot',
    version: 'Versiot',
    search: 'Hae',
    theme: 'Teema',
    noOutline: 'Ei jäsennystietoja',
    noVerseFound: 'Jae ei löytynyt',
    noFootnoteFound: 'Huomautusta ei löytynyt',
    cancel: 'Peruuta',
    chapterLabel: 'Luku',
    verseLabel: 'Jae',
    clearCache: 'Tyhjennä välimuisti',
    clearingCache: 'Tyhjennetään...',
    mergedVerseNote: ' yhdistetty edelliseen.',
    copyVerse: 'Kopioi jae',
    highlightVerse: 'Korosta',
    unhighlightVerse: 'Poista korostus',
    imageGen: 'Kuva',
  },
  'fr': {
    ...EN_UI_TEXT,
    loading: 'Chargement...',
    error: 'Erreur',
    searchPlaceholder: 'Rechercher des versets',
    verses: 'Versets',
    footnotes: 'Notes',
    allBooks: 'Tous les livres',
    noSearchResult: 'Aucun résultat trouvé',
    searchHint: 'Tapez pour rechercher des versets',
    noBookResult: 'Aucun résultat dans ce livre',
    toc: 'Sommaire',
    bookmarks: 'Signets',
    notes: 'Notes',
    chooseBook: 'Veuillez sélectionner un livre',
    oldTestament: 'AT',
    newTestament: 'NT',
    topic: 'Sujet',
    themeLight: 'Clair',
    themeDark: 'Sombre',
    themeClassic: 'Classique',
    titleChapterPrefix: 'Chapitre ',
    titleChapterSuffix: '',
    versionPicker: 'Choisir les versions',
    version: 'Versions',
    search: 'Rechercher',
    theme: 'Thème',
    noOutline: 'Aucune donnée de plan',
    noVerseFound: 'Verset introuvable',
    noFootnoteFound: 'Annotation introuvable',
    cancel: 'Annuler',
    chapterLabel: 'Chapitre',
    verseLabel: 'Verset',
    clearCache: 'Vider le cache',
    clearingCache: 'Nettoyage...',
    mergedVerseNote: ' fusionné avec le précédent.',
    copyVerse: 'Copier le verset',
    highlightVerse: 'Surligner',
    unhighlightVerse: 'Enlever le surlignage',
    imageGen: 'Image',
  },
  'hu': {
    ...EN_UI_TEXT,
    loading: 'Betöltés...',
    error: 'Hiba',
    searchPlaceholder: 'Versek keresése',
    verses: 'Versek',
    footnotes: 'Jegyzetek',
    allBooks: 'Minden könyv',
    noSearchResult: 'Nincs találat',
    searchHint: 'Gépeljen a versek kereséséhez',
    noBookResult: 'Ebben a könyvben nincs találat',
    toc: 'Tartalom',
    bookmarks: 'Könyvjelzők',
    notes: 'Jegyzetek',
    chooseBook: 'Válasszon könyvet',
    oldTestament: 'ÓSZ',
    newTestament: 'ÚSZ',
    topic: 'Téma',
    themeLight: 'Világos',
    themeDark: 'Sötét',
    themeClassic: 'Klasszikus',
    titleChapterPrefix: 'Fejezet ',
    titleChapterSuffix: '',
    versionPicker: 'Válasszon fordításokat',
    version: 'Fordítások',
    search: 'Keresés',
    theme: 'Téma',
    noOutline: 'Nincs vázlatadat',
    noVerseFound: 'A vers nem található',
    noFootnoteFound: 'A megjegyzés nem található',
    cancel: 'Mégse',
    chapterLabel: 'Fejezet',
    verseLabel: 'Vers',
    clearCache: 'Gyorsítótár törlése',
    clearingCache: 'Törlés...',
    mergedVerseNote: ' az előzővel összevonva.',
    copyVerse: 'Vers másolása',
    highlightVerse: 'Kiemelés',
    unhighlightVerse: 'Kiemelés eltávolítása',
    imageGen: 'Kép',
  },
  'it': {
    ...EN_UI_TEXT,
    loading: 'Caricamento...',
    error: 'Errore',
    searchPlaceholder: 'Cerca versetti',
    verses: 'Versetti',
    footnotes: 'Note',
    allBooks: 'Tutti i libri',
    noSearchResult: 'Nessun risultato trovato',
    searchHint: 'Digita per cercare versetti',
    noBookResult: 'Nessun risultato in questo libro',
    toc: 'Indice',
    bookmarks: 'Segnalibri',
    notes: 'Note',
    chooseBook: 'Seleziona un libro',
    oldTestament: 'AT',
    newTestament: 'NT',
    topic: 'Tema',
    themeLight: 'Chiaro',
    themeDark: 'Scuro',
    themeClassic: 'Classico',
    titleChapterPrefix: 'Capitolo ',
    titleChapterSuffix: '',
    versionPicker: 'Scegli versioni',
    version: 'Versioni',
    search: 'Cerca',
    theme: 'Tema',
    noOutline: 'Nessun dato dello schema',
    noVerseFound: 'Versetto non trovato',
    noFootnoteFound: 'Annotazione non trovata',
    cancel: 'Annulla',
    chapterLabel: 'Capitolo',
    verseLabel: 'Versetto',
    clearCache: 'Svuota cache',
    clearingCache: 'Svuotamento...',
    mergedVerseNote: ' unito al precedente.',
    copyVerse: 'Copia versetto',
    highlightVerse: 'Evidenzia',
    unhighlightVerse: 'Rimuovi evidenziazione',
    imageGen: 'Immagine',
  },
  'ja': {
    ...EN_UI_TEXT,
    loading: '読み込み中...',
    error: 'エラー',
    searchPlaceholder: '聖句を検索',
    verses: '節',
    footnotes: '注解',
    allBooks: 'すべての書巻',
    noSearchResult: '一致する結果がありません',
    searchHint: 'キーワードを入力して聖句を検索',
    noBookResult: 'この書巻には一致する結果がありません',
    toc: '目次',
    bookmarks: 'しおり',
    notes: 'ノート',
    chooseBook: '書巻を選択してください',
    oldTestament: '旧約',
    newTestament: '新約',
    topic: '主題',
    themeLight: '明るい',
    themeDark: '夜間',
    themeClassic: 'クラシック',
    titleChapterPrefix: '第',
    titleChapterSuffix: '章',
    versionPicker: '聖書版を選択',
    version: '版',
    search: '検索',
    theme: 'テーマ',
    noOutline: 'アウトラインデータはありません',
    noVerseFound: '該当する聖句が見つかりません',
    noFootnoteFound: '該当する注解が見つかりません',
    cancel: 'キャンセル',
    chapterLabel: '章',
    verseLabel: '節',
    clearCache: 'キャッシュをクリア',
    clearingCache: 'クリア中...',
    mergedVerseNote: ' 前節に統合。',
    copyVerse: '聖句をコピー',
    highlightVerse: 'ハイライト',
    unhighlightVerse: 'ハイライト解除',
    imageGen: '画像生成',
  },
  'ko': {
    ...EN_UI_TEXT,
    loading: '불러오는 중...',
    error: '오류',
    searchPlaceholder: '구절 검색',
    verses: '절',
    footnotes: '주석',
    allBooks: '모든 책',
    noSearchResult: '검색 결과가 없습니다',
    searchHint: '키워드를 입력해 구절을 검색하세요',
    noBookResult: '이 책에는 결과가 없습니다',
    toc: '목차',
    bookmarks: '책갈피',
    notes: '노트',
    chooseBook: '책을 선택하세요',
    oldTestament: '구약',
    newTestament: '신약',
    topic: '주제',
    themeLight: '밝게',
    themeDark: '야간',
    themeClassic: '클래식',
    titleChapterPrefix: '제',
    titleChapterSuffix: '장',
    versionPicker: '성경 버전 선택',
    version: '버전',
    search: '검색',
    theme: '테마',
    noOutline: '개요 데이터 없음',
    noVerseFound: '관련 구절을 찾을 수 없습니다',
    noFootnoteFound: '관련 주석을 찾을 수 없습니다',
    cancel: '취소',
    chapterLabel: '장',
    verseLabel: '절',
    clearCache: '캐시 지우기',
    clearingCache: '지우는 중...',
    mergedVerseNote: ' 앞 절과 병합됨.',
    copyVerse: '구절 복사',
    highlightVerse: '강조 표시',
    unhighlightVerse: '강조 해제',
    imageGen: '이미지',
  },
  'lt': {
    ...EN_UI_TEXT,
    loading: 'Įkeliama...',
    error: 'Klaida',
    searchPlaceholder: 'Ieškoti eilučių',
    verses: 'Eilutės',
    footnotes: 'Pastabos',
    allBooks: 'Visos knygos',
    noSearchResult: 'Rezultatų nerasta',
    searchHint: 'Įveskite tekstą eilučių paieškai',
    noBookResult: 'Šioje knygoje rezultatų nėra',
    toc: 'Turinys',
    bookmarks: 'Žymės',
    notes: 'Pastabos',
    chooseBook: 'Pasirinkite knygą',
    oldTestament: 'ST',
    newTestament: 'NT',
    topic: 'Tema',
    themeLight: 'Šviesi',
    themeDark: 'Tamsi',
    themeClassic: 'Klasikinė',
    titleChapterPrefix: 'Skyrius ',
    titleChapterSuffix: '',
    versionPicker: 'Pasirinkite versijas',
    version: 'Versijos',
    search: 'Ieškoti',
    theme: 'Tema',
    noOutline: 'Nėra struktūros duomenų',
    noVerseFound: 'Eilutė nerasta',
    noFootnoteFound: 'Pastaba nerasta',
    cancel: 'Atšaukti',
    chapterLabel: 'Skyrius',
    verseLabel: 'Eilutė',
    clearCache: 'Išvalyti podėlį',
    clearingCache: 'Valoma...',
    mergedVerseNote: ' sujungta su ankstesne.',
    copyVerse: 'Kopijuoti eilutę',
    highlightVerse: 'Paryškinti',
    unhighlightVerse: 'Panaikinti paryškinimą',
    imageGen: 'Paveikslėlis',
  },
  'lv': {
    ...EN_UI_TEXT,
    loading: 'Ielādē...',
    error: 'Kļūda',
    searchPlaceholder: 'Meklēt pantus',
    verses: 'Panti',
    footnotes: 'Piezīmes',
    allBooks: 'Visas grāmatas',
    noSearchResult: 'Nekas netika atrasts',
    searchHint: 'Ierakstiet, lai meklētu pantus',
    noBookResult: 'Šajā grāmatā nav rezultātu',
    toc: 'Saturs',
    bookmarks: 'Grāmatzīmes',
    notes: 'Piezīmes',
    chooseBook: 'Lūdzu, izvēlieties grāmatu',
    oldTestament: 'VT',
    newTestament: 'JT',
    topic: 'Tēma',
    themeLight: 'Gaišs',
    themeDark: 'Tumšs',
    themeClassic: 'Klasisks',
    titleChapterPrefix: 'Nodaļa ',
    titleChapterSuffix: '',
    versionPicker: 'Izvēlieties versijas',
    version: 'Versijas',
    search: 'Meklēt',
    theme: 'Tēma',
    noOutline: 'Nav struktūras datu',
    noVerseFound: 'Pants nav atrasts',
    noFootnoteFound: 'Piezīme nav atrasta',
    cancel: 'Atcelt',
    chapterLabel: 'Nodaļa',
    verseLabel: 'Pants',
    clearCache: 'Notīrīt kešatmiņu',
    clearingCache: 'Notīra...',
    mergedVerseNote: ' apvienots ar iepriekšējo.',
    copyVerse: 'Kopēt pantu',
    highlightVerse: 'Izcelt',
    unhighlightVerse: 'Noņemt izcēlumu',
    imageGen: 'Attēls',
  },
  'my': {
    ...EN_UI_TEXT,
    loading: 'တင်နေသည်...',
    error: 'အမှား',
    searchPlaceholder: 'ကျမ်းပိုဒ်ရှာရန်',
    verses: 'ကျမ်းပိုဒ်များ',
    footnotes: 'မှတ်ချက်များ',
    allBooks: 'စာအုပ်အားလုံး',
    noSearchResult: 'ကိုက်ညီသောရလဒ်မရှိပါ',
    searchHint: 'ကျမ်းပိုဒ်ရှာရန် စကားလုံးရိုက်ထည့်ပါ',
    noBookResult: 'ဤစာအုပ်တွင် ရလဒ်မရှိပါ',
    toc: 'မာတိကာ',
    bookmarks: 'စာမှတ်များ',
    notes: 'မှတ်စုများ',
    chooseBook: 'စာအုပ်တစ်အုပ်ရွေးပါ',
    oldTestament: 'ဟောင်း',
    newTestament: 'သစ်',
    topic: 'ခေါင်းစဉ်',
    themeLight: 'အလင်း',
    themeDark: 'အမှောင်',
    themeClassic: 'ဂန္တဝင်',
    titleChapterPrefix: 'အခန်း ',
    titleChapterSuffix: '',
    versionPicker: 'ကျမ်းစာဗားရှင်းရွေးရန်',
    version: 'ဗားရှင်းများ',
    search: 'ရှာဖွေ',
    theme: 'အပြင်အဆင်',
    noOutline: 'အကြမ်းဖျဉ်းဒေတာမရှိပါ',
    noVerseFound: 'ကျမ်းပိုဒ်မတွေ့ပါ',
    noFootnoteFound: 'မှတ်ချက်မတွေ့ပါ',
    cancel: 'မလုပ်တော့ပါ',
    chapterLabel: 'အခန်း',
    verseLabel: 'ကျမ်းပိုဒ်',
    clearCache: 'ကက်ရှ်ရှင်းလင်းမည်',
    clearingCache: 'ရှင်းလင်းနေသည်...',
    mergedVerseNote: ' ယခင်ပိုဒ်နှင့် ပေါင်းထားသည်။',
    copyVerse: 'ကူးယူမည်',
    highlightVerse: 'အမှတ်အသား',
    unhighlightVerse: 'အမှတ်အသားဖျက်မည်',
    imageGen: 'ပုံ',
  },
  'nl': {
    ...EN_UI_TEXT,
    loading: 'Laden...',
    error: 'Fout',
    searchPlaceholder: 'Verzen zoeken',
    verses: 'Verzen',
    footnotes: 'Notities',
    allBooks: 'Alle boeken',
    noSearchResult: 'Geen resultaten gevonden',
    searchHint: 'Typ om verzen te zoeken',
    noBookResult: 'Geen resultaten in dit boek',
    toc: 'Inhoud',
    bookmarks: 'Bladwijzers',
    notes: 'Notities',
    chooseBook: 'Kies een boek',
    oldTestament: 'OT',
    newTestament: 'NT',
    topic: 'Onderwerp',
    themeLight: 'Licht',
    themeDark: 'Donker',
    themeClassic: 'Klassiek',
    titleChapterPrefix: 'Hoofdstuk ',
    titleChapterSuffix: '',
    versionPicker: 'Kies versies',
    version: 'Versies',
    search: 'Zoeken',
    theme: 'Thema',
    noOutline: 'Geen overzichtsgegevens',
    noVerseFound: 'Vers niet gevonden',
    noFootnoteFound: 'Annotatie niet gevonden',
    cancel: 'Annuleren',
    chapterLabel: 'Hoofdstuk',
    verseLabel: 'Vers',
    clearCache: 'Cache wissen',
    clearingCache: 'Wissen...',
    mergedVerseNote: ' samengevoegd met de vorige.',
    copyVerse: 'Vers kopiëren',
    highlightVerse: 'Markeren',
    unhighlightVerse: 'Markering verwijderen',
    imageGen: 'Afbeelding',
  },
  'no': {
    ...EN_UI_TEXT,
    loading: 'Laster...',
    error: 'Feil',
    searchPlaceholder: 'Søk vers',
    verses: 'Vers',
    footnotes: 'Notater',
    allBooks: 'Alle bøker',
    noSearchResult: 'Ingen treff funnet',
    searchHint: 'Skriv for å søke i vers',
    noBookResult: 'Ingen treff i denne boken',
    toc: 'Innhold',
    bookmarks: 'Bokmerker',
    notes: 'Notater',
    chooseBook: 'Velg en bok',
    oldTestament: 'GT',
    newTestament: 'NT',
    topic: 'Tema',
    themeLight: 'Lys',
    themeDark: 'Mørk',
    themeClassic: 'Klassisk',
    titleChapterPrefix: 'Kapittel ',
    titleChapterSuffix: '',
    versionPicker: 'Velg versjoner',
    version: 'Versjoner',
    search: 'Søk',
    theme: 'Tema',
    noOutline: 'Ingen disposisjonsdata',
    noVerseFound: 'Fant ikke verset',
    noFootnoteFound: 'Fant ikke noten',
    cancel: 'Avbryt',
    chapterLabel: 'Kapittel',
    verseLabel: 'Vers',
    clearCache: 'Tøm hurtigbuffer',
    clearingCache: 'Tømmer...',
    mergedVerseNote: ' slått sammen med forrige.',
    copyVerse: 'Kopier vers',
    highlightVerse: 'Uthev',
    unhighlightVerse: 'Fjern utheving',
    imageGen: 'Bilde',
  },
  'pl': {
    ...EN_UI_TEXT,
    loading: 'Ładowanie...',
    error: 'Błąd',
    searchPlaceholder: 'Szukaj wersetów',
    verses: 'Wersety',
    footnotes: 'Przypisy',
    allBooks: 'Wszystkie księgi',
    noSearchResult: 'Nie znaleziono wyników',
    searchHint: 'Wpisz, aby wyszukać wersety',
    noBookResult: 'Brak wyników w tej księdze',
    toc: 'Spis treści',
    bookmarks: 'Zakładki',
    notes: 'Notatki',
    chooseBook: 'Wybierz księgę',
    oldTestament: 'ST',
    newTestament: 'NT',
    topic: 'Temat',
    themeLight: 'Jasny',
    themeDark: 'Ciemny',
    themeClassic: 'Klasyczny',
    titleChapterPrefix: 'Rozdział ',
    titleChapterSuffix: '',
    versionPicker: 'Wybierz przekłady',
    version: 'Przekłady',
    search: 'Szukaj',
    theme: 'Motyw',
    noOutline: 'Brak danych konspektu',
    noVerseFound: 'Nie znaleziono wersetu',
    noFootnoteFound: 'Nie znaleziono przypisu',
    cancel: 'Anuluj',
    chapterLabel: 'Rozdział',
    verseLabel: 'Werset',
    clearCache: 'Wyczyść pamięć podręczną',
    clearingCache: 'Czyszczenie...',
    mergedVerseNote: ' połączono z poprzednim.',
    copyVerse: 'Kopiuj werset',
    highlightVerse: 'Wyróżnij',
    unhighlightVerse: 'Usuń wyróżnienie',
    imageGen: 'Obraz',
  },
  'pt': {
    ...EN_UI_TEXT,
    loading: 'Carregando...',
    error: 'Erro',
    searchPlaceholder: 'Buscar versículos',
    verses: 'Versículos',
    footnotes: 'Notas',
    allBooks: 'Todos os livros',
    noSearchResult: 'Nenhum resultado encontrado',
    searchHint: 'Digite para buscar versículos',
    noBookResult: 'Nenhum resultado neste livro',
    toc: 'Conteúdo',
    bookmarks: 'Marcadores',
    notes: 'Notas',
    chooseBook: 'Selecione um livro',
    oldTestament: 'AT',
    newTestament: 'NT',
    topic: 'Tema',
    themeLight: 'Claro',
    themeDark: 'Escuro',
    themeClassic: 'Clássico',
    titleChapterPrefix: 'Capítulo ',
    titleChapterSuffix: '',
    versionPicker: 'Escolher versões',
    version: 'Versões',
    search: 'Buscar',
    theme: 'Tema',
    noOutline: 'Sem dados de esboço',
    noVerseFound: 'Versículo não encontrado',
    noFootnoteFound: 'Anotação não encontrada',
    cancel: 'Cancelar',
    chapterLabel: 'Capítulo',
    verseLabel: 'Versículo',
    clearCache: 'Limpar cache',
    clearingCache: 'Limpando...',
    mergedVerseNote: ' mesclado com o anterior.',
    copyVerse: 'Copiar versículo',
    highlightVerse: 'Destacar',
    unhighlightVerse: 'Remover destaque',
    imageGen: 'Imagem',
  },
  'ru': {
    ...EN_UI_TEXT,
    loading: 'Загрузка...',
    error: 'Ошибка',
    searchPlaceholder: 'Поиск стихов',
    verses: 'Стихи',
    footnotes: 'Примечания',
    allBooks: 'Все книги',
    noSearchResult: 'Ничего не найдено',
    searchHint: 'Введите текст для поиска стихов',
    noBookResult: 'В этой книге нет результатов',
    toc: 'Содержание',
    bookmarks: 'Закладки',
    notes: 'Заметки',
    chooseBook: 'Выберите книгу',
    oldTestament: 'ВЗ',
    newTestament: 'НЗ',
    topic: 'Тема',
    themeLight: 'Светлая',
    themeDark: 'Тёмная',
    themeClassic: 'Классическая',
    titleChapterPrefix: 'Глава ',
    titleChapterSuffix: '',
    versionPicker: 'Выбрать версии',
    version: 'Версии',
    search: 'Поиск',
    theme: 'Тема',
    noOutline: 'Нет данных плана',
    noVerseFound: 'Стих не найден',
    noFootnoteFound: 'Примечание не найдено',
    cancel: 'Отмена',
    chapterLabel: 'Глава',
    verseLabel: 'Стих',
    clearCache: 'Очистить кэш',
    clearingCache: 'Очистка...',
    mergedVerseNote: ' объединён с предыдущим.',
    copyVerse: 'Копировать стих',
    highlightVerse: 'Выделить',
    unhighlightVerse: 'Снять выделение',
    imageGen: 'Изображение',
  },
  'sl': {
    ...EN_UI_TEXT,
    loading: 'Nalaganje...',
    error: 'Napaka',
    searchPlaceholder: 'Išči verze',
    verses: 'Verzi',
    footnotes: 'Opombe',
    allBooks: 'Vse knjige',
    noSearchResult: 'Ni zadetkov',
    searchHint: 'Vnesite besedilo za iskanje verzov',
    noBookResult: 'V tej knjigi ni zadetkov',
    toc: 'Kazalo',
    bookmarks: 'Zaznamki',
    notes: 'Opombe',
    chooseBook: 'Izberite knjigo',
    oldTestament: 'SZ',
    newTestament: 'NZ',
    topic: 'Tema',
    themeLight: 'Svetlo',
    themeDark: 'Temno',
    themeClassic: 'Klasično',
    titleChapterPrefix: 'Poglavje ',
    titleChapterSuffix: '',
    versionPicker: 'Izberite prevode',
    version: 'Prevodi',
    search: 'Išči',
    theme: 'Tema',
    noOutline: 'Ni podatkov orisa',
    noVerseFound: 'Verza ni bilo mogoče najti',
    noFootnoteFound: 'Opombe ni bilo mogoče najti',
    cancel: 'Prekliči',
    chapterLabel: 'Poglavje',
    verseLabel: 'Verz',
    clearCache: 'Počisti predpomnilnik',
    clearingCache: 'Čiščenje...',
    mergedVerseNote: ' združen s prejšnjim.',
    copyVerse: 'Kopiraj verz',
    highlightVerse: 'Označi',
    unhighlightVerse: 'Odstrani oznako',
    imageGen: 'Slika',
  },
  'sq': {
    ...EN_UI_TEXT,
    loading: 'Duke ngarkuar...',
    error: 'Gabim',
    searchPlaceholder: 'Kërko vargje',
    verses: 'Vargje',
    footnotes: 'Shënime',
    allBooks: 'Të gjitha librat',
    noSearchResult: 'Nuk u gjetën rezultate',
    searchHint: 'Shkruani për të kërkuar vargje',
    noBookResult: 'Nuk ka rezultate në këtë libër',
    toc: 'Përmbajtja',
    bookmarks: 'Faqeshënues',
    notes: 'Shënime',
    chooseBook: 'Ju lutemi zgjidhni një libër',
    oldTestament: 'DV',
    newTestament: 'DR',
    topic: 'Temë',
    themeLight: 'E ndritshme',
    themeDark: 'E errët',
    themeClassic: 'Klasike',
    titleChapterPrefix: 'Kapitulli ',
    titleChapterSuffix: '',
    versionPicker: 'Zgjidh versionet',
    version: 'Versionet',
    search: 'Kërko',
    theme: 'Temë',
    noOutline: 'Nuk ka të dhëna skeme',
    noVerseFound: 'Vargu nuk u gjet',
    noFootnoteFound: 'Shënimi nuk u gjet',
    cancel: 'Anulo',
    chapterLabel: 'Kapitulli',
    verseLabel: 'Vargu',
    clearCache: 'Pastro memorien e fshehtë',
    clearingCache: 'Po pastrohet...',
    mergedVerseNote: ' i bashkuar me të mëparshmin.',
    copyVerse: 'Kopjo vargun',
    highlightVerse: 'Thekso',
    unhighlightVerse: 'Hiq theksimin',
    imageGen: 'Imazh',
  },
  'sr': {
    ...EN_UI_TEXT,
    loading: 'Учитавање...',
    error: 'Грешка',
    searchPlaceholder: 'Претражи стихове',
    verses: 'Стихови',
    footnotes: 'Белешке',
    allBooks: 'Све књиге',
    noSearchResult: 'Нема резултата',
    searchHint: 'Унесите текст за претрагу стихова',
    noBookResult: 'Нема резултата у овој књизи',
    toc: 'Садржај',
    bookmarks: 'Обележивачи',
    notes: 'Белешке',
    chooseBook: 'Изаберите књигу',
    oldTestament: 'СЗ',
    newTestament: 'НЗ',
    topic: 'Тема',
    themeLight: 'Светло',
    themeDark: 'Тамно',
    themeClassic: 'Класично',
    titleChapterPrefix: 'Глава ',
    titleChapterSuffix: '',
    versionPicker: 'Изаберите верзије',
    version: 'Верзије',
    search: 'Претрага',
    theme: 'Тема',
    noOutline: 'Нема података о скици',
    noVerseFound: 'Стих није пронађен',
    noFootnoteFound: 'Белешка није пронађена',
    cancel: 'Откажи',
    chapterLabel: 'Глава',
    verseLabel: 'Стих',
    clearCache: 'Обриши кеш',
    clearingCache: 'Брисање...',
    mergedVerseNote: ' спојено са претходним.',
    copyVerse: 'Копирај стих',
    highlightVerse: 'Истакни',
    unhighlightVerse: 'Уклони истицање',
    imageGen: 'Слика',
  },
  'sv': {
    ...EN_UI_TEXT,
    loading: 'Laddar...',
    error: 'Fel',
    searchPlaceholder: 'Sök verser',
    verses: 'Verser',
    footnotes: 'Noter',
    allBooks: 'Alla böcker',
    noSearchResult: 'Inga resultat hittades',
    searchHint: 'Skriv för att söka verser',
    noBookResult: 'Inga resultat i denna bok',
    toc: 'Innehåll',
    bookmarks: 'Bokmärken',
    notes: 'Anteckningar',
    chooseBook: 'Välj en bok',
    oldTestament: 'GT',
    newTestament: 'NT',
    topic: 'Ämne',
    themeLight: 'Ljus',
    themeDark: 'Mörk',
    themeClassic: 'Klassisk',
    titleChapterPrefix: 'Kapitel ',
    titleChapterSuffix: '',
    versionPicker: 'Välj versioner',
    version: 'Versioner',
    search: 'Sök',
    theme: 'Tema',
    noOutline: 'Inga dispositionsdata',
    noVerseFound: 'Vers hittades inte',
    noFootnoteFound: 'Anteckning hittades inte',
    cancel: 'Avbryt',
    chapterLabel: 'Kapitel',
    verseLabel: 'Vers',
    clearCache: 'Rensa cache',
    clearingCache: 'Rensar...',
    mergedVerseNote: ' sammanfogad med föregående.',
    copyVerse: 'Kopiera vers',
    highlightVerse: 'Markera',
    unhighlightVerse: 'Ta bort markering',
    imageGen: 'Bild',
  },
  'vi': {
    ...EN_UI_TEXT,
    loading: 'Đang tải...',
    error: 'Lỗi',
    searchPlaceholder: 'Tìm câu Kinh Thánh',
    verses: 'Câu',
    footnotes: 'Ghi chú',
    allBooks: 'Tất cả sách',
    noSearchResult: 'Không tìm thấy kết quả',
    searchHint: 'Nhập từ khóa để tìm câu Kinh Thánh',
    noBookResult: 'Không có kết quả trong sách này',
    toc: 'Mục lục',
    bookmarks: 'Dấu trang',
    notes: 'Ghi chú',
    chooseBook: 'Vui lòng chọn một sách',
    oldTestament: 'CƯ',
    newTestament: 'TƯ',
    topic: 'Chủ đề',
    themeLight: 'Sáng',
    themeDark: 'Tối',
    themeClassic: 'Cổ điển',
    titleChapterPrefix: 'Chương ',
    titleChapterSuffix: '',
    versionPicker: 'Chọn bản dịch',
    version: 'Bản dịch',
    search: 'Tìm kiếm',
    theme: 'Chủ đề',
    noOutline: 'Không có dữ liệu dàn ý',
    noVerseFound: 'Không tìm thấy câu Kinh Thánh',
    noFootnoteFound: 'Không tìm thấy chú giải',
    cancel: 'Hủy',
    chapterLabel: 'Chương',
    verseLabel: 'Câu',
    clearCache: 'Xóa bộ nhớ đệm',
    clearingCache: 'Đang xóa...',
    mergedVerseNote: ' đã gộp với câu trước.',
    copyVerse: 'Sao chép câu',
    highlightVerse: 'Đánh dấu',
    unhighlightVerse: 'Bỏ đánh dấu',
    imageGen: 'Hình ảnh',
  },
};

const getUiLanguage = (language?: string) => {
  const normalized = language?.toLowerCase();
  return normalized && UI_TEXT[normalized] ? normalized : 'en';
};

const TTS_LANGUAGE_MAP: Record<string, string> = {
  'zh': 'zh-CN',
  'zh-hant': 'zh-TW',
  'en': 'en-US',
  'cs': 'cs-CZ',
  'da': 'da-DK',
  'de': 'de-DE',
  'el': 'el-GR',
  'es': 'es-ES',
  'fi': 'fi-FI',
  'fr': 'fr-FR',
  'hu': 'hu-HU',
  'it': 'it-IT',
  'ja': 'ja-JP',
  'ko': 'ko-KR',
  'lt': 'lt-LT',
  'lv': 'lv-LV',
  'my': 'my-MM',
  'nl': 'nl-NL',
  'no': 'nb-NO',
  'pl': 'pl-PL',
  'pt': 'pt-PT',
  'ru': 'ru-RU',
  'sl': 'sl-SI',
  'sq': 'sq-AL',
  'sr': 'sr-RS',
  'sv': 'sv-SE',
  'vi': 'vi-VN',
};

const getUtteranceLang = (versionLanguage?: string, fallbackLanguage: string = 'en') => {
  const normalized = versionLanguage?.toLowerCase();
  if (normalized && TTS_LANGUAGE_MAP[normalized]) {
    return TTS_LANGUAGE_MAP[normalized];
  }
  return TTS_LANGUAGE_MAP[fallbackLanguage] || 'en-US';
};

const pickSpeechVoice = (voices: SpeechSynthesisVoice[], lang: string) => {
  const normalizedLang = lang.toLowerCase();
  const baseLanguage = normalizedLang.split('-')[0];
  const sameLanguageVoices = voices.filter(voice => {
    const voiceLang = voice.lang.toLowerCase();
    return voiceLang === normalizedLang || voiceLang === baseLanguage || voiceLang.startsWith(`${baseLanguage}-`);
  });

  return sameLanguageVoices.find(voice => voice.lang.toLowerCase() === normalizedLang)
    || sameLanguageVoices.find(voice => voice.default)
    || sameLanguageVoices[0]
    || null;
};

const App = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const debouncedCheckRef = useRef(
    debounce((element: HTMLElement) => {
      const { scrollTop } = element;
      if (scrollTop < 1387) {
        setBtnposition('old');
      } else {
        setBtnposition('new');
      }
    }, 150)
  );
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    debouncedCheckRef.current(element);
  }, []);

  const locale = useValue(db, 'locale');
  const localeOverride = (() => {
    try {
      return new URLSearchParams(window.location.search).get('locale');
    } catch {
      return null;
    }
  })();
  const effectiveLocale = localeOverride || locale;
  const defaults = resolveBibleDefaults(effectiveLocale);
  const availableVersions = getAvailableVersions();
  const supportedLanguageCodes = new Set(['zh', 'zh-hant', 'en', 'cs', 'da', 'de', 'el', 'es', 'fi', 'fr', 'hu', 'it', 'ja', 'ko', 'lt', 'lv', 'my', 'nl', 'no', 'pl', 'pt', 'ru', 'sl', 'sq', 'sr', 'sv', 'vi']);
  const normalizedLocale = (effectiveLocale || 'en').toLowerCase();
  const currentLanguage = normalizedLocale.startsWith('zh')
    ? (normalizedLocale.includes('tw') || normalizedLocale.includes('hk') || normalizedLocale.includes('hant') ? 'zh-hant' : 'zh')
    : normalizedLocale.split('-')[0];
  const resolvedLanguage = supportedLanguageCodes.has(currentLanguage) ? currentLanguage : 'en';
  const baseLanguage = resolvedLanguage;
  const shouldPrioritizeByLanguage = baseLanguage !== 'zh' && baseLanguage !== 'zh-hant';
  const getVersionPriority = (version: { language: string }, index: number) => {
    const languagePriority = shouldPrioritizeByLanguage && version.language === baseLanguage ? 0 : 1;
    return [languagePriority, index] as const;
  };
  const sortedAvailableVersions = [...availableVersions].sort((a, b) => {
    const aIndex = availableVersions.findIndex(v => v.id === a.id);
    const bIndex = availableVersions.findIndex(v => v.id === b.id);
    const [aLangPriority, aOrder] = getVersionPriority(a, aIndex);
    const [bLangPriority, bOrder] = getVersionPriority(b, bIndex);
    return aLangPriority - bLangPriority || aOrder - bOrder;
  });
  const versionOrder = new Map(sortedAvailableVersions.map((version, index) => [version.id, index]));
  const sortVersionIds = (ids: string[]) => [...ids].sort((a, b) => (versionOrder.get(a) ?? 999) - (versionOrder.get(b) ?? 999));
  const VERSION_SELECTION_KEY = 'bible-selected-versions';
  const savedVersionIds = (() => {
    try {
      const raw = localStorage.getItem(VERSION_SELECTION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const validIds = parsed.filter((id): id is string => typeof id === 'string' && versionOrder.has(id));
          const uniqueIds = Array.from(new Set(validIds));
          if (uniqueIds.length > 0) {
            return sortVersionIds(uniqueIds).slice(0, 5);
          }
        }
      }
    } catch {}
    return [defaults.versionId || 'KJV'];
  })();

  const [versionIds, setVersionIds] = useState<string[]>(savedVersionIds);
  const [versionPickerOpen, setVersionPickerOpen] = useState(false);
  const [bibles, setBibles] = useState<any>([]);
  const [loadedBookVerses, setLoadedBookVerses] = useState<Record<string, any[]>>({});
  const [modalLoadedBookVerses, setModalLoadedBookVerses] = useState<Record<string, any[]>>({});
  const [modalLoadingBookVerses, setModalLoadingBookVerses] = useState<Record<string, boolean>>({});

  const primaryVersionId = versionIds[0] || 'ChiRcVs';
  const annotationVersionId = versionIds.find(v => v.startsWith('ChiRcV')) || primaryVersionId;
  const primaryVersionMeta = availableVersions.find(v => v.id === primaryVersionId);
  const uiLanguage = getUiLanguage(primaryVersionMeta?.language);
  const useChineseChapterNumerals = uiLanguage === 'zh' || uiLanguage === 'zh-hant';
  const t = UI_TEXT[uiLanguage];

  const VERSION_COLORS = ['inherit', '#2a6496', '#4a8c3f', '#c0573a', '#8b5cf6'];
  const isRecoveryVersionId = (versionId: string) => versionId === 'ChiRcVs' || versionId === 'ChiRcV';
  const [loading, setLoading] = useState(true);
  const [clearingCache, setClearingCache] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const READING_POSITION_KEY = 'bible-reading-position';
  const THEME_KEY = 'bible-theme';
  const savedTheme = (() => {
    try {
      const raw = localStorage.getItem(THEME_KEY);
      if (raw && ['light', 'dark', 'classic'].includes(raw)) return raw as 'light' | 'dark' | 'classic';
    } catch {}
    return 'classic';
  })();
  const savedPosition = (() => {
    try {
      const raw = localStorage.getItem(READING_POSITION_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p && p.bookId && p.chapter) return p;
      }
    } catch {}
    return { bookId: '1', chapter: 1, verse: '1' };
  })();

  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState<string>(savedPosition.bookId);
  const [selectedChapter, setSelectedChapter] = useState<number>(savedPosition.chapter);
  const [selectedVerse, setSelectedVerse] = useState<string>(savedPosition.verse || '1');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchTab, setSearchTab] = useState<'verses' | 'footnotes'>('verses');
  const [searchVerseResults, setSearchVerseResults] = useState<any[]>([]);
  const [searchFootnoteResults, setSearchFootnoteResults] = useState<any[]>([]);
  const [searchVerseDisplay, setSearchVerseDisplay] = useState(20);
  const [searchFootnoteDisplay, setSearchFootnoteDisplay] = useState(20);
  const [searchBookFilter, setSearchBookFilter] = useState<string | null>(null);
  const [searchBookDropdownOpen, setSearchBookDropdownOpen] = useState(false);
  const sidebarSearchInputRef = useRef<HTMLInputElement>(null);
  const searchBodyRef = useRef<HTMLDivElement>(null);
  const searchBookDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!searchBookDropdownOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!searchBookDropdownRef.current?.contains(event.target as Node)) {
        setSearchBookDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [searchBookDropdownOpen]);

  const [fontSize, setFontSize] = useState(14);
  const [theme, setTheme] = useState<'light' | 'dark' | 'classic'>(savedTheme);
  const [btnposition, setBtnposition] = useState('old');
  const [speechPlayState, setSpeechPlayState] = useState<'idle' | 'playing' | 'paused'>('idle');
  const [currentSpeakingVerse, setCurrentSpeakingVerse] = useState<string | null>(null);
  const [speechVoices, setSpeechVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [speechVoicesLoaded, setSpeechVoicesLoaded] = useState(false);
  const activeUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const speechRequestRef = useRef(0);
  const currentSegmentIndexRef = useRef(0);
  const wakeLockRef = useRef<any>(null);
  const isTtsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; verseText: string; verseKey: string } | null>(null);
  const [highlightedVerses, setHighlightedVerses] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('bible-highlighted-verses');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Modal 堆栈系统
  type FootnoteData = { _id?: string; seq: string; note: string; verse: string; location: number; flag?: string };
  type VerseRef = { bookId: string; chapter: number; verse: string };
  type AnnRef = { bookId: string; chapter: number; verse: string; seq: string; endVerse?: string; flag?: string; sourceFootnoteId?: string; sourceLocation?: number; label?: string };
  type VerseRangeItem = {
    bookId: string;
    bookName: string;
    chapterStart: number;
    verseStart: number;
    chapterEnd: number;
    verseEnd: number;
    flagStart?: string;
    flagEnd?: string;
  };
  type GroupedParenEntry =
    | { id: string; label: string; kind: 'verse'; bookId: string; chapter: number; verse: string }
    | { id: string; label: string; kind: 'ann'; annRef: AnnRef }
    | { id: string; label: string; kind: 'range'; bookId: string; verseRanges: VerseRangeItem[]; annRef?: AnnRef };
  type ModalDescriptor = { id: string; bookId: string; chapter?: number; footnotes?: FootnoteData[]; verseRef?: VerseRef; annRef?: AnnRef; verseRanges?: VerseRangeItem[]; rangeLabel?: string; groupedParenEntries?: GroupedParenEntry[]; groupedParenLabel?: string };
  const [modalStack, setModalStack] = useState<ModalDescriptor[]>([]);
  const pushModal = (modal: ModalDescriptor) => setModalStack(prev => [...prev, modal]);
  const popModal = () => setModalStack(prev => prev.slice(0, -1));

  const getVerseFlagSortOrder = (flag?: string) => {
    switch (String(flag || '0')) {
      case '0':
        return 0;
      case '1':
        return 1;
      case '3':
        return 2;
      case '2':
        return 3;
      default: {
        const parsed = Number.parseInt(String(flag || '0'), 10);
        return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed + 10;
      }
    }
  };

  const compareVersesByPosition = (a: any, b: any) =>
    Number.parseInt(String(a.Verse), 10) - Number.parseInt(String(b.Verse), 10)
    || getVerseFlagSortOrder(a.Flag) - getVerseFlagSortOrder(b.Flag);

  const getVerseFlagSpan = (flag?: string): { start: number; end: number } => {
    switch (String(flag || '0')) {
      case '1':
        return { start: 0, end: 1 };
      case '3':
        return { start: 1, end: 2 };
      case '2':
        return { start: 2, end: 3 };
      default:
        return { start: 0, end: 3 };
    }
  };

  const doesVerseOverlapRange = (range: VerseRangeItem, chapter: number, verseNum: number, flag?: string) => {
    if (verseNum < range.verseStart || verseNum > range.verseEnd) return false;

    const span = getVerseFlagSpan(flag);
    let requestedStart = 0;
    let requestedEnd = 3;

    if (chapter === range.chapterStart && verseNum === range.verseStart && range.flagStart) {
      requestedStart = getVerseFlagSpan(range.flagStart).start;
    }

    if (chapter === range.chapterEnd && verseNum === range.verseEnd && range.flagEnd) {
      requestedEnd = getVerseFlagSpan(range.flagEnd).end;
    }

    return span.end > requestedStart && span.start < requestedEnd;
  };

  const mainContentRef = useRef<HTMLDivElement>(null);
  const nextModalId = useRef(0);

  const scrollMainToTop = useCallback(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => {
      try {
        localStorage.setItem(
          READING_POSITION_KEY,
          JSON.stringify({ bookId: selectedBookId, chapter: selectedChapter, verse: selectedVerse }),
        );
      } catch {}
    }, 200);
    return () => window.clearTimeout(id);
  }, [selectedBookId, selectedChapter, selectedVerse]);

  useEffect(() => {
    try {
      localStorage.setItem(VERSION_SELECTION_KEY, JSON.stringify(versionIds));
    } catch {}
  }, [versionIds]);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {}
  }, [theme]);

  const handleClearBibleCache = async () => {
    try {
      setClearingCache(true);
      clearBibleCache();
      setBibles({});
      setLoadedBookVerses({});
      setModalLoadedBookVerses({});
      setModalLoadingBookVerses({});
      setError(null);
      setLoading(true);
      const reloaded: any = {};
      for (const versionId of versionIds) {
        reloaded[versionId] = await loadBibleData(versionId);
      }
      setBibles(reloaded);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear cache');
    } finally {
      setLoading(false);
      setClearingCache(false);
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(VERSION_SELECTION_KEY);
      if (raw) {
        setVersionIds(prev => sortVersionIds(prev).slice(0, 5));
      } else {
        const defaultVersionId = defaults.versionId || 'KJV';
        setVersionIds([defaultVersionId]);
      }
    } catch {
      const defaultVersionId = defaults.versionId || 'KJV';
      setVersionIds([defaultVersionId]);
    }
  }, [effectiveLocale]);

  const versionIdsRef = useRef(versionIds);
  versionIdsRef.current = versionIds;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const signal = controller.signal;

    (async () => {
      // 先用函数式更新获取当前 bibles，只标记需要加载的版本
      const toLoad = await new Promise<string[]>((resolve) => {
        setBibles((prev: any) => {
          const missing = versionIds.filter(v => !prev || !prev[v]);
          resolve(missing);
          return prev || {};
        });
      });

      if (cancelled || toLoad.length === 0) {
        if (!cancelled) setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // 使用 loadBibleMetadata：只加载书籍目录等元数据，不加载全部经文
        for (const versionId of toLoad) {
          if (cancelled || signal.aborted) break;
          const data = await loadBibleMetadata(versionId);
          if (cancelled || signal.aborted) break;
          // 只加载当前 versionIds 中仍然存在的版本
          if (versionIdsRef.current.includes(versionId)) {
            setBibles((prev: any) => ({ ...prev, [versionId]: data }));
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '加载失败');
          console.error('加载圣经数据失败:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [versionIds]);

  // 当前书卷变化时，按需加载该书卷的经文
  useEffect(() => {
    if (!selectedBookId || !bibles || !bibles[primaryVersionId]?.books) return;

    const loadVersesForBook = async () => {
      const primaryBooks = bibles[primaryVersionId].books;
      const primaryBook = primaryBooks.find((b: any) => b._id === selectedBookId);
      const primaryBookIndex = primaryBook ? primaryBooks.findIndex((b: any) => b._id === primaryBook._id) : 0;
      const ordinalBookId = String(primaryBookIndex + 1);

      const newVerses: Record<string, any[]> = {};
      for (const versionId of versionIds) {
        if (!bibles[versionId]) continue;
        const vBooks = bibles[versionId].books || [];

        // 找到该书卷在此版本中的对应 book 引用
        let vBook = vBooks.find((b: any) => b._id === selectedBookId);
        if (!vBook && primaryBookIndex < vBooks.length) {
          vBook = vBooks[primaryBookIndex];
        }
        const bookRefs = vBook
          ? [vBook.book_id, vBook._id, vBook.name, vBook.acronym_name, ordinalBookId].filter(Boolean) as string[]
          : [ordinalBookId, primaryBook?._id, selectedBookId].filter(Boolean) as string[];

        for (const ref of bookRefs) {
          const chunk = await loadBibleBook(versionId, ref);
          if (chunk.length > 0) {
            newVerses[versionId] = chunk;
            break;
          }
        }
        if (!newVerses[versionId]) newVerses[versionId] = [];
      }
      setLoadedBookVerses(newVerses);
    };

    loadVersesForBook();
  }, [selectedBookId, primaryVersionId, bibles, versionIds]);

  // 预加载相邻书卷的经文，使翻书切换即瞬间显示
  useEffect(() => {
    if (!selectedBookId || !bibles || !bibles[primaryVersionId]?.books) return;

    const prefetchAdjacent = async () => {
      const primaryBooks = bibles[primaryVersionId].books;
      const currentIdx = primaryBooks.findIndex((b: any) => b._id === selectedBookId);
      if (currentIdx < 0) return;

      const adjacentIndices = [currentIdx + 1, currentIdx - 1].filter(
        i => i >= 0 && i < primaryBooks.length
      );

      for (const verId of versionIds) {
        const vBooks = bibles[verId]?.books;
        if (!vBooks) continue;
        for (const idx of adjacentIndices) {
          const book = vBooks[idx] || primaryBooks[idx];
          if (!book) continue;
          const refs = [book.book_id, book._id].filter(Boolean) as string[];
          for (const ref of refs) {
            try { await loadBibleBook(verId, ref); } catch {}
          }
        }
      }
    };

    const timer = window.setTimeout(() => prefetchAdjacent(), 500);
    return () => window.clearTimeout(timer);
  }, [selectedBookId, primaryVersionId, bibles, versionIds]);

  // 从 URL 参数 ?bk=book_name&ch=chapter 读取初始定位（来自 SEO 页面跳转）
  // 使用 ref 确保 URL 参数只在首次成功解析时生效，之后不再重复处理
  const urlParamsResolved = useRef(false);
  useEffect(() => {
    // 等待至少有一个版本的 books 数据加载完成
    if (!bibles) return;
    const search = window.location.search;
    if (!search || urlParamsResolved.current) return;
    try {
      const params = new URLSearchParams(search);
      const bkRaw = params.get('bk');
      const ch = params.get('ch');
      if (bkRaw && ch) {
        // decodeURIComponent handles double-encoded values (e.g. %2520 → space)
        let bk = bkRaw;
        try { bk = decodeURIComponent(bkRaw); } catch {}
        // also try decoding again in case of double-encoding
        try { bk = decodeURIComponent(bk); } catch {}
        const bkLower = bk.toLowerCase().replace(/[_\s]+/g, ' ').trim();
        // 跨所有已加载版本搜索匹配的书卷——因为 URL 参数中的书名可能来自
        // 任意版本的 book_id（如英文版 "Revelation of John" vs 中文版 "Revelation"）
        let targetBook: any = null;
        const loadedVersionIds = Object.keys(bibles).filter(v => bibles[v]?.books?.length > 0);
        for (const versionId of loadedVersionIds) {
          const books = bibles[versionId].books;
          targetBook = books.find((b: any) => {
            const fields = [b.book_id, b.name, b.acronym_name].filter(Boolean);
            return fields.some((f: string) =>
              f.toLowerCase().replace(/[_\s]+/g, ' ').trim() === bkLower
            );
          });
          if (targetBook) break;
        }
        if (targetBook) {
          urlParamsResolved.current = true;
          setSelectedBookId(targetBook._id);
          setSelectedChapter(parseInt(ch) || 1);
          // 清除 URL 参数，避免刷新后重复触发
          window.history.replaceState({}, document.title, '/');
        } else {
          // 检查是否所有请求的版本都已加载完毕
          const allLoaded = versionIds.length > 0 && versionIds.every(v => bibles[v]?.books?.length > 0);
          if (allLoaded) {
            // 所有版本已加载完毕但仍未匹配，标记已处理并清除参数以允许 savedPosition 生效
            urlParamsResolved.current = true;
            window.history.replaceState({}, document.title, '/');
          }
        }
      }
    } catch {}
  }, [bibles, primaryVersionId]);

  // 切换版本后，如果 selectedBookId 在新版本中不存在，回退到第一卷
  useEffect(() => {
    if (loading || !bibles || !bibles[primaryVersionId]?.books) return;
    const exists = bibles[primaryVersionId].books.some((b: any) => b._id === selectedBookId);
    if (!exists) {
      const firstBook = bibles[primaryVersionId].books[0];
      if (firstBook) {
        setSelectedBookId(firstBook._id);
        setSelectedChapter(1);
        setSelectedVerse('1');
      }
    }
  }, [primaryVersionId, loading, bibles]);
  const getPrimaryBookByVerseBook = (verseBook: string) => {
    if (!bibles || !bibles[primaryVersionId]?.books) return undefined;
    return bibles[primaryVersionId].books.find((b: any) => b.book_id === verseBook || b._id === verseBook);
  };

  const getBookName = (version: string, bookId: string) => {
    if (!bibles || !bibles[version]?.books) return '';
    return bibles[version]?.books.find((b: any) => b._id === bookId)?.name || '';
  };

  // 中文数字 → 阿拉伯数字
  const chineseNumToInt = (s: string): number | null => {
    const m: Record<string, number> = { '一':1, '二':2, '三':3, '四':4, '五':5, '六':6, '七':7, '八':8, '九':9, '零':0, '○':0, '十':10, '百':100 };
    if (!s) return null;
    if (s.length === 1) return m[s] ?? null;
    // 无十/百的缩写形式：按十进制位值展开（如 二八→28, 三五→35）
    const hasPlace = s.includes('十') || s.includes('百');
    if (!hasPlace) {
      let result = 0;
      for (const ch of s) {
        const n = m[ch];
        if (n === undefined || n >= 10) return null;
        result = result * 10 + n;
      }
      return result || null;
    }
    // 标准形式：有十/百
    let r = 0, t = 0;
    for (const ch of s) {
      const n = m[ch];
      if (n === undefined) return null;
      if (n >= 10) { t = (t || 1) * n; r += t; t = 0; }
      else { t = n; }
    }
    r += t;
    return r || null;
  };

  // 解析脚注文本中的经文引用，返回可点击的 React 节点
  const parseRefs = (
    text: string,
    curBookId: string,
    curChapter: number,
    curVerse?: string,
    curFlag?: string,
    sourceFootnoteId?: string,
    sourceLocation?: number,
  ): React.ReactNode[] => {
    if (!bibles || !bibles[primaryVersionId]?.books) return [text];
    const books: any[] = bibles[primaryVersionId].books;
    const acronyms = books
      .filter((b: any) => b.acronym_name)
      .map((b: any) => ({ a: b.acronym_name, b }))
      .sort((x: any, y: any) => y.a.length - x.a.length);
    const fullNames = books
      .map((b: any) => ({ n: b.name, b }))
      .sort((x: any, y: any) => y.n.length - x.n.length);
    const cnNums = '一二三四五六七八九十百零○';
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const abbrPat = acronyms.map((x: any) => esc(x.a)).join('|');
    const fullNamePat = fullNames.map((x: any) => esc(x.n)).join('|');
    const referencePrefixRe = /^[参參]\s*/;
    const noteConnectorPattern = '[与與]';
    const noteMarkerPattern = '[注註]';
    const verseMarkerPattern = '[节節]';
    const noteJoinPattern = `(?:\\s*${noteConnectorPattern}\\s*)?`;
    const stripReferencePrefix = (value: string) => value.replace(referencePrefixRe, '');
    const getMatchedLabel = (matchText: string, prefixLength = 0) => matchText.slice(prefixLength);
    const parseVerseFlag = (value?: string): string | undefined => {
      if (value === '上') return '1';
      if (value === '下') return '2';
      if (value === '中') return '3';
      return undefined;
    };
    const parseRangeEnd = (value: string): { chapterEnd: number | null; verseEnd: number; flagEnd?: string } | null => {
      const singleVerseMatch = /^(\d+)([上下中])?$/.exec(value);
      if (singleVerseMatch) {
        return {
          chapterEnd: null,
          verseEnd: parseInt(singleVerseMatch[1]),
          flagEnd: parseVerseFlag(singleVerseMatch[2]),
        };
      }
      const match = new RegExp(`^([${cnNums}]+|\\d+)(\\d+)([上下中])?$`).exec(value);
      if (!match) return null;
      const chapterEnd = /^\\d+$/.test(match[1]) ? parseInt(match[1]) : chineseNumToInt(match[1]);
      if (chapterEnd === null) return null;
      return { chapterEnd, verseEnd: parseInt(match[2]), flagEnd: parseVerseFlag(match[3]) };
    };
    const parseVerseOnlyRangeEnd = (value: string): { verseEnd: number; flagEnd?: string } | null => {
      const match = /^(\d+)([上下中])?$/.exec(value);
      if (!match) return null;
      return {
        verseEnd: parseInt(match[1], 10),
        flagEnd: parseVerseFlag(match[2]),
      };
    };
    const verseOnlyRefRe = new RegExp(`^(\\d+)([上下中])?(?:[～~](\\d+[上下中]?))?(?:${verseMarkerPattern})?$`);
    const parseCurrentChapterVerseRanges = (
      segments: string[],
      chapter: number,
      book?: { b: any } | null,
    ): VerseRangeItem[] | null => {
      const ranges: VerseRangeItem[] = [];
      for (const seg of segments) {
        const trimmed = stripReferencePrefix(seg.trim());
        if (!trimmed) continue;
        const verseOnlyMatch = verseOnlyRefRe.exec(trimmed);
        if (!verseOnlyMatch) return null;
        const verseStart = parseInt(verseOnlyMatch[1], 10);
        const flagStart = parseVerseFlag(verseOnlyMatch[2]);
        let verseEnd = verseStart;
        let flagEnd = flagStart;
        if (verseOnlyMatch[3]) {
          const rangeEnd = parseVerseOnlyRangeEnd(verseOnlyMatch[3]);
          if (!rangeEnd) return null;
          verseEnd = rangeEnd.verseEnd;
          flagEnd = rangeEnd.flagEnd;
        }
        ranges.push({
          bookId: book?.b._id || curBookId,
          bookName: book?.b.name || getBookName(primaryVersionId, curBookId),
          chapterStart: chapter,
          verseStart,
          chapterEnd: chapter,
          verseEnd,
          flagStart,
          flagEnd,
        });
      }
      return ranges.length > 0 ? ranges : null;
    };

    interface RefMatch { index: number; end: number; bookId?: string; bookName?: string; chapter?: number; verse?: string; endVerse?: string; annSeq?: string | null; label: string; isRange?: boolean; ranges?: VerseRangeItem[]; explicitBook?: boolean; groupedEntries?: RefMatch[]; }
    const matches: RefMatch[] = [];
    const compoundSegmentLeadRe = new RegExp(`^(?:${fullNamePat}|${abbrPat}|[${cnNums}]+\\d+|\\d)`);
    const splitCompoundReferenceSegment = (value: string): string[] => {
      const parts: string[] = [];
      let start = 0;
      for (let i = 0; i < value.length; i += 1) {
        const ch = value[i];
        if (ch !== '与' && ch !== '與') continue;
        const before = value.slice(start, i).trimEnd();
        const after = value.slice(i + 1).trimStart();
        if (!before || !after) continue;
        if (!/[\d上下中]$/.test(before)) continue;
        if (/^[注註]/.test(after)) continue;
        if (!compoundSegmentLeadRe.test(after)) continue;
        const segment = before.trim();
        if (segment) parts.push(segment);
        start = i + 1;
      }
      const tail = value.slice(start).trim();
      if (tail) parts.push(tail);
      return parts;
    };
    const refReWithCh = new RegExp(`^([${cnNums}]+|\\d+)(\\d+)([上下中])?(?:[～~]([${cnNums}\\d]+[上下中]?))?$`);
    const trailingAnnotationBodyPattern = `(?:${noteMarkerPattern}(\\d+)|(\\d+)${noteMarkerPattern}(\\d+))`;
    const optionalTrailingAnnotationPattern = `(?:${noteJoinPattern}${trailingAnnotationBodyPattern})?`;
    const chapterVerseAnnRe = new RegExp(`^([${cnNums}]+)(\\d+)(?:${verseMarkerPattern})?(?:${noteJoinPattern}${noteMarkerPattern}(\\d+))$`);
    const verseOnlyAnnRe = new RegExp(`^(\\d+)(?:${noteJoinPattern}${noteMarkerPattern}(\\d+))$`);
    const resolveTrailingAnnotation = (
      startVerse: string,
      endVerse: string,
      directSeq?: string | null,
      explicitVerse?: string | null,
      explicitSeq?: string | null,
    ) => {
      if (explicitVerse && explicitSeq) {
        return { annSeq: explicitSeq, annVerse: explicitVerse, annEndVerse: explicitVerse };
      }
      return { annSeq: directSeq || null, annVerse: startVerse, annEndVerse: endVerse };
    };
    const splitParentheticalGroups = (value: string): { text: string; start: number; end: number }[] => {
      const groups: { text: string; start: number; end: number }[] = [];
      let start = 0;
      for (let i = 0; i < value.length; i += 1) {
        if (!/[，,、]/.test(value[i])) continue;
        groups.push({ text: value.slice(start, i), start, end: i });
        start = i + 1;
      }
      groups.push({ text: value.slice(start), start, end: value.length });
      return groups;
    };
    const parseParentheticalGroup = (
      value: string,
      initialBook: any = null,
      initialChapter: number | null = null,
    ): {
      ranges: VerseRangeItem[];
      annRefData: { bookId: string; bookName: string; chapter: number; verse: string; endVerse?: string; seq: string } | null;
      hasExplicitBook: boolean;
      nextBook: any;
      nextChapter: number | null;
    } => {
      const ranges: VerseRangeItem[] = [];
      let annRefData: { bookId: string; bookName: string; chapter: number; verse: string; endVerse?: string; seq: string } | null = null;
      const segs = splitCompoundReferenceSegment(value)
        .flatMap(seg => seg.split(/[，,、\s]+/g))
        .map(seg => seg.trim())
        .filter(Boolean);
      let parenCtxBook: any = initialBook;
      let parenCtxChapter: number | null = initialChapter;
      let hasExplicitBook = false;
      for (const seg of segs) {
        const trimmed = seg.trim();
        const normalized = stripReferencePrefix(trimmed);
        if (!normalized || /^[与與]$/.test(normalized)) continue;
        let book: any = null;
        let refPart = normalized;
        for (const fn of fullNames) {
          if (normalized.startsWith(fn.n)) { book = fn; refPart = normalized.slice(fn.n.length); break; }
        }
        if (!book) {
          for (const ac of acronyms) {
            if (normalized.startsWith(ac.a)) { book = ac; refPart = normalized.slice(ac.a.length); break; }
          }
        }

        const chapterVerseAnnMatch = chapterVerseAnnRe.exec(refPart);
        if (chapterVerseAnnMatch?.[3]) {
          const chapterValue = chapterVerseAnnMatch[1];
          const parsedChapter = /^\d+$/.test(chapterValue) ? parseInt(chapterValue, 10) : chineseNumToInt(chapterValue);
          if (parsedChapter !== null) {
            const annBookId = book?.b._id || parenCtxBook?.b._id || curBookId;
            const annBookName = book?.b.name || parenCtxBook?.b.name || getBookName(primaryVersionId, curBookId);
            annRefData = {
              bookId: annBookId,
              bookName: annBookName,
              chapter: parsedChapter,
              verse: chapterVerseAnnMatch[2],
              endVerse: chapterVerseAnnMatch[2],
              seq: chapterVerseAnnMatch[3],
            };
            if (book) {
              hasExplicitBook = true;
              parenCtxBook = book;
            }
            parenCtxChapter = parsedChapter;
            continue;
          }
        }

        const verseOnlyAnnMatch = verseOnlyAnnRe.exec(refPart);
        if (verseOnlyAnnMatch?.[2]) {
          const annChapter = parenCtxChapter ?? curChapter;
          const annBookId = book?.b._id || parenCtxBook?.b._id || curBookId;
          const annBookName = book?.b.name || parenCtxBook?.b.name || getBookName(primaryVersionId, curBookId);
          annRefData = {
            bookId: annBookId,
            bookName: annBookName,
            chapter: annChapter,
            verse: verseOnlyAnnMatch[1],
            endVerse: verseOnlyAnnMatch[1],
            seq: verseOnlyAnnMatch[2],
          };
          if (book) {
            hasExplicitBook = true;
            parenCtxBook = book;
          }
          continue;
        }

        const verseOnlyMatch = !book ? verseOnlyRefRe.exec(refPart) : null;
        if (verseOnlyMatch) {
          const verseStart = parseInt(verseOnlyMatch[1], 10);
          const flagStart = parseVerseFlag(verseOnlyMatch[2]);
          let verseEnd = verseStart;
          let flagEnd = flagStart;
          if (verseOnlyMatch[3]) {
            const rangeEnd = parseVerseOnlyRangeEnd(verseOnlyMatch[3]);
            if (!rangeEnd) continue;
            verseEnd = rangeEnd.verseEnd;
            flagEnd = rangeEnd.flagEnd;
          }
          const targetChapter = parenCtxChapter ?? curChapter;
          const targetBookId = parenCtxBook?.b._id || curBookId;
          const targetBookName = parenCtxBook?.b.name || getBookName(primaryVersionId, curBookId);
          ranges.push({ bookId: targetBookId, bookName: targetBookName, chapterStart: targetChapter, verseStart, chapterEnd: targetChapter, verseEnd, flagStart, flagEnd });
          continue;
        }

        const sm = refReWithCh.exec(refPart);
        if (!sm) continue;
        const chStart = /^\d+$/.test(sm[1]) ? parseInt(sm[1], 10) : chineseNumToInt(sm[1]);
        if (chStart === null) continue;
        const vStart = parseInt(sm[2], 10);
        const flagStart = parseVerseFlag(sm[3]);
        let chEnd = chStart;
        let vEnd = vStart;
        let flagEnd = flagStart;
        if (sm[4]) {
          const rangeEnd = parseRangeEnd(sm[4]);
          if (!rangeEnd) continue;
          if (rangeEnd.chapterEnd !== null) chEnd = rangeEnd.chapterEnd;
          vEnd = rangeEnd.verseEnd;
          flagEnd = rangeEnd.flagEnd;
        }
        if (book) {
          hasExplicitBook = true;
          parenCtxBook = book;
          parenCtxChapter = chStart;
          ranges.push({ bookId: book.b._id, bookName: book.b.name, chapterStart: chStart, verseStart: vStart, chapterEnd: chEnd, verseEnd: vEnd, flagStart, flagEnd });
        } else if (parenCtxBook) {
          parenCtxChapter = chStart;
          ranges.push({ bookId: parenCtxBook.b._id, bookName: parenCtxBook.b.name, chapterStart: chStart, verseStart: vStart, chapterEnd: chEnd, verseEnd: vEnd, flagStart, flagEnd });
        } else {
          parenCtxChapter = chStart;
          ranges.push({ bookId: curBookId, bookName: getBookName(primaryVersionId, curBookId), chapterStart: chStart, verseStart: vStart, chapterEnd: chEnd, verseEnd: vEnd, flagStart, flagEnd });
        }
      }
      return { ranges, annRefData, hasExplicitBook, nextBook: parenCtxBook, nextChapter: parenCtxChapter };
    };

    // 模式0: 括号括起的复合注解引用与经文范围引用（全角/半角括号均支持）
    // 优先识别整段括号注解引用，如（启二二16与注4）
    const parenRe = new RegExp(`([（(])([^）)]*(?:${fullNamePat}|${abbrPat}|[${cnNums}]+\\d+|\\d+~\\d+|\\d+～\\d+)[^）)]*)([）)])`, 'g');
    const innerAnnRe = new RegExp(`^([${cnNums}]+)(\\d+)(?:${verseMarkerPattern})?(?:${noteJoinPattern}${noteMarkerPattern}(\\d+))$`);
    const currentChapterListParenRe = new RegExp(`([（(])((?:[参參]\\s*)?\\d+(?:\\s*[，,、]\\s*\\d+)*)([）)])`, 'g');
    for (let mpList: RegExpExecArray | null; (mpList = currentChapterListParenRe.exec(text)) !== null; ) {
      const listSegments = stripReferencePrefix(mpList[2].trim()).split(/[，,、]/g);
      const listRanges = parseCurrentChapterVerseRanges(listSegments, curChapter, null);
      if (!listRanges) continue;
      matches.push({
        index: mpList.index,
        end: mpList.index + mpList[0].length,
        isRange: true,
        label: mpList[0],
        explicitBook: false,
        ranges: listRanges,
      });
    }
    for (let mp: RegExpExecArray | null; (mp = parenRe.exec(text)) !== null; ) {
      const inner = mp[2];
      const normalizedInner = stripReferencePrefix(inner.trim());
      let annBook: any = null;
      let annRefPart = normalizedInner;
      for (const fn of fullNames) {
        if (normalizedInner.startsWith(fn.n)) { annBook = fn; annRefPart = normalizedInner.slice(fn.n.length); break; }
      }
      if (!annBook) {
        for (const ac of acronyms) {
          if (normalizedInner.startsWith(ac.a)) { annBook = ac; annRefPart = normalizedInner.slice(ac.a.length); break; }
        }
      }
      const innerAnnMatch = innerAnnRe.exec(annRefPart);
      if (innerAnnMatch?.[3]) {
        const ch = chineseNumToInt(innerAnnMatch[1]);
        if (ch !== null) {
          matches.push({ index: mp.index, end: mp.index + mp[0].length, bookId: annBook?.b._id, bookName: annBook?.b.name, chapter: ch, verse: innerAnnMatch[2], annSeq: innerAnnMatch[3], label: mp[0], explicitBook: !!annBook });
          continue;
        }
      }

      let parenCtxBook: any = null;
      let parenCtxChapter: number | null = null;
      const groupedEntries: RefMatch[] = [];
      for (const group of splitParentheticalGroups(inner)) {
        const rawGroup = group.text;
        const trimmedGroup = rawGroup.trim();
        if (!trimmedGroup) continue;

        const prefixLength = trimmedGroup.match(referencePrefixRe)?.[0].length || 0;
        const leadingWhitespace = rawGroup.length - rawGroup.trimStart().length;
        const trailingWhitespace = rawGroup.length - rawGroup.trimEnd().length;
        const groupIndex = mp.index + mp[1].length + group.start + leadingWhitespace + prefixLength;
        const groupEnd = mp.index + mp[1].length + group.end - trailingWhitespace;
        const label = getMatchedLabel(trimmedGroup, prefixLength);
        if (!label || groupEnd <= groupIndex) continue;

        const { ranges, annRefData, hasExplicitBook, nextBook, nextChapter } = parseParentheticalGroup(trimmedGroup, parenCtxBook, parenCtxChapter);
        parenCtxBook = nextBook;
        parenCtxChapter = nextChapter;

        if (ranges.length > 0) {
          groupedEntries.push({
            index: groupIndex,
            end: groupEnd,
            bookId: annRefData?.bookId,
            bookName: annRefData?.bookName,
            chapter: annRefData?.chapter,
            verse: annRefData?.verse,
            endVerse: annRefData?.endVerse,
            annSeq: annRefData?.seq,
            isRange: true,
            label,
            explicitBook: hasExplicitBook,
            ranges,
          });
          continue;
        }

        if (annRefData) {
          groupedEntries.push({
            index: groupIndex,
            end: groupEnd,
            bookId: annRefData.bookId,
            bookName: annRefData.bookName,
            chapter: annRefData.chapter,
            verse: annRefData.verse,
            endVerse: annRefData.endVerse,
            annSeq: annRefData.seq,
            label,
            explicitBook: hasExplicitBook,
          });
        }
      }

      if (groupedEntries.length === 0) continue;
      matches.push({
        index: mp.index,
        end: mp.index + mp[0].length,
        label: mp[0],
        explicitBook: groupedEntries.some(entry => !!entry.explicitBook),
        groupedEntries,
      });
    }

    // 模式1: [参/參]? 书卷缩写 中文章节 阿拉伯节号 [～阿拉伯结束节号]? [节/節]? [[与/與]注/註N]?
    const re1 = new RegExp(`([参參]\\s*)?(${abbrPat})([${cnNums}]+)(\\d+)(?:[～~](\\d+))?(${verseMarkerPattern})?${optionalTrailingAnnotationPattern}`, 'g');
    for (let m1: RegExpExecArray | null; (m1 = re1.exec(text)) !== null; ) {
      const ac = acronyms.find((x: any) => x.a === m1[2]);
      if (!ac) continue;
      const ch = chineseNumToInt(m1[3]);
      if (ch === null) continue;
      const rangeEndVerse = m1[5] || m1[4];
      const { annSeq, annVerse, annEndVerse } = resolveTrailingAnnotation(m1[4], rangeEndVerse, m1[7], m1[8], m1[9]);
      const label = getMatchedLabel(m1[0], m1[1]?.length || 0);
      matches.push({ index: m1.index + (m1[1]?.length || 0), end: m1.index + m1[0].length, bookId: ac.b._id, bookName: ac.b.name, chapter: ch, verse: annSeq ? annVerse : m1[4], endVerse: annSeq ? annEndVerse : rangeEndVerse, annSeq, label, explicitBook: true, isRange: !!m1[5], ranges: m1[5] ? [{ bookId: ac.b._id, bookName: ac.b.name, chapterStart: ch, verseStart: parseInt(m1[4]), chapterEnd: ch, verseEnd: parseInt(m1[5]) }] : undefined });
    }

    // 模式2: [参/參]? 中文章节 阿拉伯节号 [～阿拉伯结束节号]? [节/節]? [[与/與]注/註N]?（无书卷缩写，继承上下文）
    const re2 = new RegExp(`([参參]\\s*)?([${cnNums}]+)(\\d+)(?:[～~](\\d+))?(${verseMarkerPattern})?${optionalTrailingAnnotationPattern}`, 'g');
    for (let m2: RegExpExecArray | null; (m2 = re2.exec(text)) !== null; ) {
      const ch = chineseNumToInt(m2[2]);
      if (ch === null) continue;
      const rangeEndVerse = m2[4] || m2[3];
      const { annSeq, annVerse, annEndVerse } = resolveTrailingAnnotation(m2[3], rangeEndVerse, m2[6], m2[7], m2[8]);
      const label = getMatchedLabel(m2[0], m2[1]?.length || 0);
      matches.push({ index: m2.index + (m2[1]?.length || 0), end: m2.index + m2[0].length, bookId: curBookId, bookName: getBookName(primaryVersionId, curBookId), chapter: ch, verse: annSeq ? annVerse : m2[3], endVerse: annSeq ? annEndVerse : rangeEndVerse, annSeq, label, explicitBook: false, isRange: !!m2[4], ranges: m2[4] ? [{ bookId: curBookId, bookName: getBookName(primaryVersionId, curBookId), chapterStart: ch, verseStart: parseInt(m2[3]), chapterEnd: ch, verseEnd: parseInt(m2[4]) }] : undefined });
    }

    // 模式3a: 阿拉伯节号～阿拉伯节号 + 节/節（纯节号范围）
    const re3a = new RegExp(`(\\d+)[～~](\\d+)(${verseMarkerPattern})`, 'g');
    for (let m3a: RegExpExecArray | null; (m3a = re3a.exec(text)) !== null; ) {
      const vStart = parseInt(m3a[1]);
      const vEnd = parseInt(m3a[2]);
      const label = m3a[0];
      matches.push({ index: m3a.index, end: m3a.index + m3a[0].length, bookId: curBookId, bookName: getBookName(primaryVersionId, curBookId), chapter: curChapter, verse: m3a[1], endVerse: m3a[2], label, explicitBook: false, isRange: true, ranges: [{ bookId: curBookId, bookName: getBookName(primaryVersionId, curBookId), chapterStart: curChapter, verseStart: vStart, chapterEnd: curChapter, verseEnd: vEnd }] });
    }

    // 模式3b: 阿拉伯节号 + 节/節 [[与/與]注/註N]?（继承上下文书卷及章）
    const re3b = new RegExp(`(\\d+)(${verseMarkerPattern})(?:${noteJoinPattern}${noteMarkerPattern}(\\d+))?`, 'g');
    for (let m3b: RegExpExecArray | null; (m3b = re3b.exec(text)) !== null; ) {
      const annSeq = m3b[3] || null;
      const label = m3b[0];
      matches.push({ index: m3b.index, end: m3b.index + m3b[0].length, verse: m3b[1], endVerse: m3b[1], annSeq, label, explicitBook: false });
    }

    // 模式3c: 阿拉伯节号 [[与/與]注/註N]（继承上下文书卷及章）
    const re3c = new RegExp(`(\\d+)(?:${noteJoinPattern}${noteMarkerPattern}(\\d+))`, 'g');
    for (let m3c: RegExpExecArray | null; (m3c = re3c.exec(text)) !== null; ) {
      const annSeq = m3c[2] || null;
      if (!annSeq) continue;
      const label = m3c[0];
      matches.push({ index: m3c.index, end: m3c.index + m3c[0].length, verse: m3c[1], endVerse: m3c[1], annSeq, label, explicitBook: false });
    }

    // 扩展匹配：将 、-分隔的后续节号并入前面的引用
    // 例如 "约一14、17" → 整体作为一个引用，ranges 含 1:14 和 1:17
    const verseOnlyWithFlag = /^[、，,](\d+)([上下中])?/;
    for (const mt of matches) {
      if (!mt.bookId && mt.chapter === undefined && !mt.ranges) continue;
      let tail = text.slice(mt.end);
      let extended = false;
      while (verseOnlyWithFlag.test(tail)) {
        const m = verseOnlyWithFlag.exec(tail)!;
        const v = m[1];
        const f = m[2];
        mt.end += m[0].length;
        mt.label = text.slice(mt.index, mt.end);

        if (!mt.ranges) {
          const rc = mt.chapter ?? curChapter;
          const rb = mt.bookId ?? curBookId;
          const rn = mt.bookName ?? getBookName(primaryVersionId, curBookId);
          mt.ranges = [{
            bookId: rb, bookName: rn,
            chapterStart: rc, verseStart: parseInt(mt.verse || '1'),
            chapterEnd: rc, verseEnd: parseInt(mt.endVerse || mt.verse || '1'),
          }];
        }

        const rc = mt.chapter ?? curChapter;
        const rb = mt.bookId ?? curBookId;
        const rn = mt.bookName ?? getBookName(primaryVersionId, curBookId);
        mt.ranges.push({
          bookId: rb, bookName: rn,
          chapterStart: rc, verseStart: parseInt(v),
          chapterEnd: rc, verseEnd: parseInt(v),
          flagStart: parseVerseFlag(f), flagEnd: parseVerseFlag(f),
        });
        mt.isRange = true;
        extended = true;
        tail = text.slice(mt.end);
      }
      // 如果有扩展，更新 endVerse 为最后一个节号
      if (extended && mt.ranges && mt.ranges.length > 0) {
        const last = mt.ranges[mt.ranges.length - 1];
        mt.endVerse = String(last.verseEnd);
      }
    }

    // 按位置排序，同位置优先长匹配；去除重叠
    matches.sort((a, b) => a.index - b.index || b.end - a.end);
    const kept: RefMatch[] = [];
    let prevEnd = 0;
    for (const mt of matches) {
      if (mt.index < prevEnd) continue;
      kept.push(mt);
      prevEnd = mt.end;
    }

    if (kept.length === 0) return [text];

    // 按出现顺序解析上下文书卷：遇到显式书卷时更新上下文，否则继承
    let contextBookId = curBookId;
    let contextBookName = getBookName(primaryVersionId, curBookId);
    let contextChapter = curChapter;
    const resolved: { mt: RefMatch; bookId: string; bookName: string; chapter: number }[] = [];
    for (const mt of kept) {
      if (mt.isRange) {
        // 括号范围引用：有显式书卷则更新上下文，否则继承
        if (mt.ranges && mt.ranges.length > 0 && mt.ranges[0].bookId) {
          contextBookId = mt.ranges[0].bookId;
          contextBookName = mt.ranges[0].bookName;
        }
        const resolvedChapter = mt.ranges?.[0]?.chapterStart ?? contextChapter;
        const lastRange = mt.ranges?.[mt.ranges.length - 1];
        if (lastRange) contextChapter = lastRange.chapterEnd;
        resolved.push({ mt, bookId: contextBookId, bookName: contextBookName, chapter: resolvedChapter });
        continue;
      }
      const resolvedChapter = mt.chapter ?? contextChapter;
      if (mt.explicitBook) {
        contextBookId = mt.bookId!;
        contextBookName = mt.bookName!;
        if (mt.chapter !== undefined) contextChapter = mt.chapter;
        resolved.push({ mt, bookId: mt.bookId!, bookName: mt.bookName!, chapter: resolvedChapter });
      } else {
        if (mt.chapter !== undefined) contextChapter = mt.chapter;
        resolved.push({ mt, bookId: contextBookId, bookName: contextBookName, chapter: resolvedChapter });
      }
    }

    const parts: React.ReactNode[] = [];
    let last = 0;
    for (const { mt, bookId, chapter } of resolved) {
      if (mt.index > last) parts.push(text.slice(last, mt.index));
      parts.push(
        <span
          key={`ref-${mt.index}`}
          className="ref-link"
          onClick={(e) => {
            e.stopPropagation();
            nextModalId.current += 1;
            const annBookId = mt.bookId || bookId;
            const annChapter = mt.chapter ?? chapter;
            if (mt.groupedEntries && mt.groupedEntries.length > 0) {
              const groupedParenEntries = mt.groupedEntries
                .map((entry, entryIndex): GroupedParenEntry | null => {
                  const entryBookId = entry.bookId || bookId;
                  const entryChapter = entry.chapter ?? chapter;

                  if (entry.isRange && entry.ranges && entry.ranges.length > 0) {
                    const rangeBookId = entryBookId || entry.ranges[0].bookId;
                    if (!rangeBookId) return null;
                    const rangeAnnBookId = entry.bookId || rangeBookId;
                    const rangeAnnChapter = entry.chapter ?? entryChapter;
                    return {
                      id: `group-${nextModalId.current}-${entryIndex}`,
                      label: entry.label,
                      kind: 'range',
                      bookId: rangeBookId,
                      verseRanges: entry.ranges,
                      annRef: entry.annSeq && rangeAnnChapter !== undefined ? {
                        bookId: rangeAnnBookId,
                        chapter: rangeAnnChapter,
                        verse: entry.verse!,
                        endVerse: entry.endVerse,
                        seq: entry.annSeq,
                        flag: rangeAnnBookId === curBookId && rangeAnnChapter === curChapter && String(entry.verse) === String(curVerse) ? curFlag : undefined,
                        sourceFootnoteId,
                        sourceLocation,
                        label: entry.label,
                      } : undefined,
                    };
                  }

                  if (entry.annSeq && entryBookId && entryChapter !== undefined) {
                    const shouldPreferCurrentFlag = entryBookId === curBookId && entryChapter === curChapter && String(entry.verse) === String(curVerse);
                    return {
                      id: `group-${nextModalId.current}-${entryIndex}`,
                      label: entry.label,
                      kind: 'ann',
                      annRef: {
                        bookId: entryBookId,
                        chapter: entryChapter,
                        verse: entry.verse!,
                        endVerse: entry.endVerse,
                        seq: entry.annSeq,
                        flag: shouldPreferCurrentFlag ? curFlag : undefined,
                        sourceFootnoteId,
                        sourceLocation,
                        label: entry.label,
                      },
                    };
                  }

                  if (entryBookId && entryChapter !== undefined && entry.verse) {
                    return {
                      id: `group-${nextModalId.current}-${entryIndex}`,
                      label: entry.label,
                      kind: 'verse',
                      bookId: entryBookId,
                      chapter: entryChapter,
                      verse: entry.verse,
                    };
                  }

                  return null;
                })
                .filter((entry): entry is GroupedParenEntry => Boolean(entry));

              if (groupedParenEntries.length === 0) return;

              const firstGroupedEntry = groupedParenEntries[0];
              pushModal({
                id: `grouped-${nextModalId.current}`,
                bookId: firstGroupedEntry.kind === 'ann' ? firstGroupedEntry.annRef.bookId : firstGroupedEntry.bookId,
                groupedParenEntries,
                groupedParenLabel: mt.label,
              });
            } else if (mt.isRange && mt.ranges) {
              pushModal({
                id: `range-${nextModalId.current}`,
                bookId: bookId || mt.ranges[0].bookId,
                verseRanges: mt.ranges,
                rangeLabel: mt.label,
                annRef: mt.annSeq ? {
                  bookId: annBookId,
                  chapter: annChapter,
                  verse: mt.verse!,
                  endVerse: mt.endVerse,
                  seq: mt.annSeq,
                  flag: annBookId === curBookId && annChapter === curChapter && String(mt.verse) === String(curVerse) ? curFlag : undefined,
                  sourceFootnoteId,
                  sourceLocation,
                  label: mt.label,
                } : undefined,
              });
            } else if (mt.annSeq) {
              const shouldPreferCurrentFlag = annBookId === curBookId && annChapter === curChapter && String(mt.verse) === String(curVerse);
              pushModal({
                id: `ann-${nextModalId.current}`,
                bookId: annBookId,
                chapter: annChapter,
                annRef: {
                  bookId: annBookId,
                  chapter: annChapter,
                  verse: mt.verse!,
                  endVerse: mt.endVerse,
                  seq: mt.annSeq,
                  flag: shouldPreferCurrentFlag ? curFlag : undefined,
                  sourceFootnoteId,
                  sourceLocation,
                  label: mt.label,
                },
              });
            } else {
              pushModal({
                id: `verse-${nextModalId.current}`,
                bookId,
                chapter,
                verseRef: { bookId, chapter, verse: mt.verse! },
              });
            }
          }}
        >
          {mt.label}
        </span>
      );
      last = mt.end;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };

  // 通过主版本的 _id 获取目标版本中对应书卷；优先按 book_id，对不上时按书卷顺序回退
  const getBookRefsForVersion = (version: string, bookId: string): string[] => {
    if (!bibles || !bibles[primaryVersionId]?.books) return [bookId];
    const primaryBooks = bibles[primaryVersionId].books;
    const primaryBookIndex = primaryBooks.findIndex((b: any) => b._id === bookId);
    const primaryBook = primaryBookIndex >= 0 ? primaryBooks[primaryBookIndex] : null;
    if (!primaryBook) return [bookId];

    const targetBooks = bibles[version]?.books || [];
    const targetBook = targetBooks.find((b: any) => b.book_id === primaryBook.book_id)
      || targetBooks[primaryBookIndex]
      || null;

    return targetBook ? [targetBook._id, targetBook.book_id].filter(Boolean) : [primaryBook.book_id];
  };

  const getBookChunkRefsForVersion = (version: string, bookId: string): string[] => {
    if (!bibles || !bibles[primaryVersionId]?.books) return [bookId];
    const primaryBooks = bibles[primaryVersionId].books;
    const primaryBookIndex = primaryBooks.findIndex((b: any) => b._id === bookId);
    const primaryBook = primaryBookIndex >= 0 ? primaryBooks[primaryBookIndex] : null;
    if (!primaryBook) return [bookId];

    const ordinalBookId = String(primaryBookIndex + 1);
    const targetBooks = bibles[version]?.books || [];
    const targetBook = targetBooks.find((b: any) => b.book_id === primaryBook.book_id)
      || targetBooks[primaryBookIndex]
      || null;

    if (!targetBook) {
      return Array.from(new Set([ordinalBookId, primaryBook.book_id, primaryBook._id, bookId].filter(Boolean)));
    }

    return Array.from(new Set([
      targetBook.book_id,
      targetBook._id,
      targetBook.name,
      targetBook.acronym_name,
      ordinalBookId,
    ].filter(Boolean)));
  };

  const getModalBookCacheKey = (version: string, bookId: string) => `${version}:${bookId}`;

  const getDisplayVersesForBook = useCallback((version: string, bookId: string) => {
    if (version === primaryVersionId && bookId === selectedBookId) {
      return loadedBookVerses[version] || [];
    }
    return modalLoadedBookVerses[getModalBookCacheKey(version, bookId)] || [];
  }, [loadedBookVerses, modalLoadedBookVerses, primaryVersionId, selectedBookId]);

  const hasDisplayVersesForBook = useCallback((version: string, bookId: string) => {
    if (version === primaryVersionId && bookId === selectedBookId) {
      return Object.prototype.hasOwnProperty.call(loadedBookVerses, version);
    }
    return Object.prototype.hasOwnProperty.call(modalLoadedBookVerses, getModalBookCacheKey(version, bookId));
  }, [loadedBookVerses, modalLoadedBookVerses, primaryVersionId, selectedBookId]);

  const isDisplayBookLoading = useCallback((version: string, bookId: string) => {
    if (version === primaryVersionId && bookId === selectedBookId) {
      return !Object.prototype.hasOwnProperty.call(loadedBookVerses, version);
    }
    return !!modalLoadingBookVerses[getModalBookCacheKey(version, bookId)];
  }, [loadedBookVerses, modalLoadingBookVerses, primaryVersionId, selectedBookId]);

  const ensureDisplayVersesForBook = useCallback(async (version: string, bookId: string) => {
    if (!bookId) return;
    if (version === primaryVersionId && bookId === selectedBookId && Object.prototype.hasOwnProperty.call(loadedBookVerses, version)) {
      return;
    }

    const cacheKey = getModalBookCacheKey(version, bookId);
    if (Object.prototype.hasOwnProperty.call(modalLoadedBookVerses, cacheKey) || modalLoadingBookVerses[cacheKey]) {
      return;
    }

    setModalLoadingBookVerses(prev => ({ ...prev, [cacheKey]: true }));
    try {
      let chunk: any[] = [];
      for (const ref of getBookChunkRefsForVersion(version, bookId)) {
        chunk = await loadBibleBook(version, ref);
        if (chunk.length > 0) break;
      }
      setModalLoadedBookVerses(prev => ({ ...prev, [cacheKey]: chunk }));
    } finally {
      setModalLoadingBookVerses(prev => {
        const next = { ...prev };
        delete next[cacheKey];
        return next;
      });
    }
  }, [getBookChunkRefsForVersion, loadedBookVerses, modalLoadedBookVerses, modalLoadingBookVerses, primaryVersionId, selectedBookId]);

  useEffect(() => {
    if (modalStack.length === 0) return;

    const top = modalStack[modalStack.length - 1];
    const targetBookIds: string[] = top.groupedParenEntries
      ? Array.from(new Set(top.groupedParenEntries.flatMap(entry => {
          if (entry.kind === 'ann') return [entry.annRef.bookId];
          if (entry.kind === 'range') {
            return [
              entry.bookId,
              ...entry.verseRanges.map(range => range.bookId),
              entry.annRef?.bookId,
            ].filter((bookId): bookId is string => Boolean(bookId));
          }
          return [entry.bookId];
        })))
      : top.verseRanges
        ? Array.from(new Set([
            ...top.verseRanges.map(rg => rg.bookId || top.bookId),
            top.annRef?.bookId,
          ].filter((bookId): bookId is string => Boolean(bookId))))
        : top.verseRef
          ? [top.verseRef.bookId]
          : top.annRef
            ? [top.annRef.bookId]
            : [];

    if (targetBookIds.length === 0) return;

    void Promise.all(targetBookIds.map(bookId => ensureDisplayVersesForBook(primaryVersionId, bookId)));
  }, [ensureDisplayVersesForBook, modalStack, primaryVersionId]);


  const getBookTopic = (version: string, bookId: string) => {
    if (!bibles || !bibles[version]?.topics) return '';
    const refs = getBookRefsForVersion(version, bookId);
    return bibles[version]?.topics.find((t: any) => refs.includes(t.book_index))?.topic || '';
  };

  const getOutlines = (version: string, bookId: string, chapter: number) => {
    if (!bibles || !bibles[version]?.outlines) return [];
    const refs = getBookRefsForVersion(version, bookId);
    return bibles[version]?.outlines
      ?.filter((o: any) => refs.includes(o.book_index) && String(o.chapter) === String(chapter))
      .sort((a: any, b: any) => parseInt(a.section) - parseInt(b.section) || parseInt(a.level) - parseInt(b.level));
  };

  const hasRenderableFootnoteNote = (note: string | undefined) => typeof note === 'string' && note.trim().length > 0;

  const getFootnotes = (version: string, bookId: string, chapter: number, verse: string, verseFlag: string) => {
    if (!bibles || !bibles[version]?.footnotes) return [];
    const refs = getBookRefsForVersion(version, bookId);
    const footnotes = bibles[version]?.footnotes
      ?.filter((f: any) =>
        refs.includes(f.book_index) &&
        String(f.chapter) === String(chapter) &&
        String(f.section) === String(verse) &&
        String(f.flag) === String(verseFlag)
      )
      .map((f: any) => ({ _id: f._id, seq: f.seq, note: f.note, location: parseInt(f.location) || 0, verse: verse, flag: verseFlag }))
      .sort((a: any, b: any) => a.location - b.location);

    const noteBySeq = new Map<string, string>();
    footnotes.forEach((fn: any) => {
      if (!noteBySeq.has(String(fn.seq)) && hasRenderableFootnoteNote(fn.note)) {
        noteBySeq.set(String(fn.seq), fn.note);
      }
    });

    return footnotes.map((fn: any) => {
      if (hasRenderableFootnoteNote(fn.note)) return fn;
      const fallbackNote = noteBySeq.get(String(fn.seq));
      return fallbackNote ? { ...fn, note: fallbackNote } : fn;
    });
  };

  const getAnnotationFootnote = (
    version: string,
    bookId: string,
    chapter: number,
    verse: string,
    seq: string,
    preferredFlag?: string,
    sourceFootnoteId?: string,
    sourceLocation?: number,
    endVerse?: string,
  ) => {
    if (!bibles || !bibles[version]?.footnotes) return null;
    const refs = getBookRefsForVersion(version, bookId);
    const baseCandidates = bibles[version].footnotes.filter((f: any) =>
      refs.includes(f.book_index) &&
      String(f.chapter) === String(chapter) &&
      String(f.section) === String(verse) &&
      String(f.seq) === String(seq)
    );
    const fallbackCandidates = endVerse != null && String(endVerse) !== String(verse)
      ? bibles[version].footnotes.filter((f: any) =>
        refs.includes(f.book_index) &&
        String(f.chapter) === String(chapter) &&
        String(f.section) === String(endVerse) &&
        String(f.seq) === String(seq)
      )
      : [];
    const candidates = baseCandidates.length > 0 ? baseCandidates : fallbackCandidates;
    const sourceMatch = candidates.find((f: any) =>
      sourceFootnoteId != null && f._id === sourceFootnoteId
    ) || candidates.find((f: any) =>
      sourceLocation != null && (parseInt(f.location) || 0) === sourceLocation
    );
    const preferredCandidates = preferredFlag == null
      ? candidates
      : candidates.filter((f: any) => String(f.flag || '0') === String(preferredFlag));

    return (sourceMatch && hasRenderableFootnoteNote(sourceMatch.note) ? sourceMatch : null)
      || preferredCandidates.find((f: any) => hasRenderableFootnoteNote(f.note))
      || candidates.find((f: any) => hasRenderableFootnoteNote(f.note))
      || sourceMatch
      || preferredCandidates[0]
      || candidates[0]
      || null;
  };

  const mergeRecoveryVerseFootnotes = (parts: any[], bookId: string, chapter: number) => {
    const merged: any[] = [];
    let textOffset = 0;

    parts.forEach((part: any) => {
      const partFlag = String(part.Flag || '0');
      const footnotes = getFootnotes(annotationVersionId, bookId, chapter, String(part.Verse), partFlag);
      footnotes.forEach((fn: any) => {
        merged.push({
          ...fn,
          location: textOffset + (fn.location || 0),
          verse: String(part.Verse),
          flag: partFlag,
        });
      });
      textOffset += String(part.Text || '').length + 1;
    });

    return merged.sort((a, b) => a.location - b.location);
  };

  // 渲染经文文本，带内联注脚标记
  const renderVerseText = (verse: any, bookId: string, chapter: number, flag: string, keyPrefix: string = '') => {
    const fns = verse.mergedRecoveryParts
      ? mergeRecoveryVerseFootnotes(verse.mergedRecoveryParts, bookId, chapter)
      : getFootnotes(annotationVersionId, bookId, chapter, verse.Verse, flag);
    if (fns.length === 0) return <span>{verse.Text}</span>;
    const text = verse.Text;
    const parts: React.ReactNode[] = [];
    let last = 0;
    fns.forEach((fn: any) => {
      const pos = fn.location - 1;
      if (pos > last) parts.push(<span key={`${keyPrefix}t-${fn._id}`}>{text.slice(last, pos)}</span>);
      parts.push(
        <sup
          key={`${keyPrefix}fn-${fn._id}`}
          className="footnote-num"
          onClick={(e) => {
            e.stopPropagation();
            nextModalId.current += 1;
            pushModal({ id: `fn-${nextModalId.current}`, bookId, chapter, footnotes: [{ _id: fn._id, seq: fn.seq, note: fn.note, verse: verse.Verse, location: fn.location, flag: String(fn.flag || flag || '0') }] });
          }}
        >
          {fn.seq}
        </sup>
      );
      last = pos;
    });
    if (last < text.length) parts.push(<span key={`${keyPrefix}t-${last}`}>{text.slice(last)}</span>);
    return <span>{parts}</span>;
  };

  const getBookIntros = (version: string, bookId: string) => {
    if (!bibles || !bibles[version]?.bookIntros) return [];
    const refs = getBookRefsForVersion(version, bookId);
    return bibles[version]?.bookIntros.filter((i: any) => refs.includes(i.book_index));
  };

  const getCurrentPrimaryChapterVerses = useCallback(() => {
    if (!selectedBookId || !selectedChapter || !bibles || !bibles[primaryVersionId]?.books) return [];
    const verses = loadedBookVerses[primaryVersionId];
    if (!verses || verses.length === 0) return [];

    const primaryBooks = bibles[primaryVersionId].books;
    const primaryBook = primaryBooks.find((b: any) => b._id === selectedBookId);
    const primaryBookRefs = primaryBook ? [primaryBook.book_id, primaryBook._id].filter(Boolean) : [];

    return verses
      .filter((v: any) => primaryBookRefs.includes(v.Book) && v.Chapter * 1 === selectedChapter)
      .sort(compareVersesByPosition);
  }, [loadedBookVerses, bibles, primaryVersionId, selectedBookId, selectedChapter]);

  const chapterSpeechSegments = useMemo(() => {
    const verses = getCurrentPrimaryChapterVerses();
    if (verses.length === 0) return [] as { text: string; verseNum: string | null }[];

    const grouped = new Map<string, string[]>();
    verses.forEach((verse: any) => {
      const text = String(verse.Text || '').trim();
      if (!text || text === 'a') return;
      const verseNum = String(verse.Verse);
      if (!grouped.has(verseNum)) grouped.set(verseNum, []);
      grouped.get(verseNum)!.push(text);
    });

    const chapterLabel = useChineseChapterNumerals
      ? `${getBookName(primaryVersionId, selectedBookId)} ${t.titleChapterPrefix}${toChineseNum(selectedChapter)}${t.titleChapterSuffix}`
      : `${getBookName(primaryVersionId, selectedBookId)} ${t.titleChapterPrefix}${selectedChapter}${t.titleChapterSuffix}`;

    const verseSegments = Array.from(grouped.entries())
      .map(([verseNum, parts]) => ({ text: (verseNum === '0' ? parts.join(' ') : parts.join(' ')).trim(), verseNum }))
      .filter(s => s.text.length > 0);

    return [{ text: chapterLabel, verseNum: null }, ...verseSegments];
  }, [getCurrentPrimaryChapterVerses, primaryVersionId, selectedBookId, selectedChapter, t.titleChapterPrefix, t.titleChapterSuffix, useChineseChapterNumerals]);

  const chapterSpeechText = useMemo(() => chapterSpeechSegments.map(s => s.text).join(' ').trim(), [chapterSpeechSegments]);
  const currentSpeechLang = useMemo(
    () => getUtteranceLang(primaryVersionMeta?.language, resolvedLanguage),
    [primaryVersionMeta?.language, resolvedLanguage],
  );
  const isMobileDevice = useMemo(() => {
    return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
  }, []);

  const browserPrimaryLang = useMemo(() => {
    return (navigator.language || 'en').split('-')[0].toLowerCase();
  }, []);

  const currentVersionLang = useMemo(() => {
    return (primaryVersionMeta?.language || 'en').toLowerCase();
  }, [primaryVersionMeta?.language]);

  const isCurrentLanguageSpeakable = useMemo(() => {
    if (!isTtsSupported) return false;

    // 手机端：简单规则，首选语言 + 英语
    if (isMobileDevice) {
      const allowed = new Set([browserPrimaryLang]);
      if (browserPrimaryLang !== 'en') allowed.add('en');
      return allowed.has(currentVersionLang);
    }

    // 桌面端：原来的 voice 检测逻辑
    if (!speechVoicesLoaded) return true;
    if (speechVoices.length === 0) return false;
    return !!pickSpeechVoice(speechVoices, currentSpeechLang);
  }, [isTtsSupported, isMobileDevice, browserPrimaryLang, currentVersionLang, speechVoicesLoaded, speechVoices, currentSpeechLang]);

  // book_id → _id 映射
  const bookIdMap = useMemo(() => {
    if (!bibles || !bibles[primaryVersionId]?.books) return {};
    const m: Record<string, string> = {};
    for (const b of bibles[primaryVersionId].books) {
      if (b.book_id) m[b.book_id] = b._id;
    }
    return m;
  }, [bibles, versionIds]);

  const doSearch = useCallback(async (q: string) => {
    if (!bibles || !bibles[primaryVersionId] || !q.trim()) {
      setSearchVerseResults([]);
      setSearchFootnoteResults([]);
      return;
    }
    // 搜索需要全部经文数据，触发完整加载
    const fullBible = await loadBibleData(primaryVersionId);
    const verseResults = (fullBible.verses || [])
      .filter((v: any) => v.Text && v.Text.includes(q));
    setSearchVerseResults(verseResults);
    setSearchVerseDisplay(20);
    // 搜注解
    const fnResults = (bibles[primaryVersionId].footnotes || [])
      .filter((f: any) => f.note && f.note.includes(q));
    setSearchFootnoteResults(fnResults);
    setSearchFootnoteDisplay(20);
  }, [bibles, primaryVersionId]);

  const doSearchRef = useRef(doSearch);
  doSearchRef.current = doSearch;

  const debouncedSearch = useRef(
    debounce((q: string) => doSearchRef.current(q), 200)
  );

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    setSearchBookFilter(null);
    setSearchBookDropdownOpen(false);
    debouncedSearch.current(val);
  };

  const openSearch = () => {
    setSidebarVisible(true);
    setSearchVisible(true);
    setSearchQuery('');
    setSearchVerseResults([]);
    setSearchFootnoteResults([]);
    setSearchBookFilter(null);
    setSearchBookDropdownOpen(false);
  };

  const exitSearchMode = () => {
    setSearchVisible(false);
    setSearchQuery('');
    setSearchVerseResults([]);
    setSearchFootnoteResults([]);
    setSearchBookFilter(null);
    setSearchBookDropdownOpen(false);
  };

  const jumpToVerseResult = (v: any) => {
    const bid = getPrimaryBookByVerseBook(v.Book)?._id || bookIdMap[v.Book] || v.Book;
    setSelectedBookId(bid);
    setSelectedChapter(v.Chapter * 1);
    setSelectedVerse(String(v.Verse));
    setSidebarVisible(false);
    setSearchVisible(false);
  };

  const loadSpeechVoices = useCallback((): Promise<SpeechSynthesisVoice[]> => {
    if (!isTtsSupported) return Promise.resolve([]);
    const synth = window.speechSynthesis;
    const existingVoices = synth.getVoices();
    if (existingVoices.length > 0) return Promise.resolve(existingVoices);
    return new Promise(resolve => {
      let done = false;
      const finish = (v: SpeechSynthesisVoice[]) => { if (!done) { done = true; resolve(v); } };
      synth.addEventListener('voiceschanged', () => { const v = synth.getVoices(); if (v.length > 0) finish(v); }, { once: true });
      setTimeout(() => finish(synth.getVoices()), 500);
    });
  }, [isTtsSupported]);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  const stopChapterSpeech = useCallback(() => {
    speechRequestRef.current += 1;
    activeUtteranceRef.current = null;
    currentSegmentIndexRef.current = 0;
    setSpeechPlayState('idle');
    setCurrentSpeakingVerse(null);
    releaseWakeLock();
    if (!isTtsSupported) return;
    const synth = window.speechSynthesis;
    synth.pause();
    synth.cancel();
    setTimeout(() => { synth.cancel(); }, 100);
  }, [isTtsSupported, releaseWakeLock]);

  const startSpeechSequence = useCallback(async (startIndex: number) => {
    const requestId = speechRequestRef.current + 1;
    speechRequestRef.current = requestId;
    currentSegmentIndexRef.current = startIndex;

    const synth = window.speechSynthesis;
    synth.cancel();
    await new Promise(r => setTimeout(r, 150));
    if (speechRequestRef.current !== requestId) return;

    // 请求屏幕常亮
    if ('wakeLock' in navigator) {
      (navigator as any).wakeLock.request('screen').then((wl: any) => {
        if (speechRequestRef.current !== requestId) { wl.release().catch(() => {}); return; }
        wakeLockRef.current = wl;
        wl.addEventListener('release', () => { wakeLockRef.current = null; });
      }).catch(() => {});
    }

    const targetLang = getUtteranceLang(primaryVersionMeta?.language, resolvedLanguage);
    const voices = await loadSpeechVoices();
    if (speechRequestRef.current !== requestId) return;

    const matchedVoice = pickSpeechVoice(voices, targetLang);

    // 桌面端：有 voice 列表但无匹配 → 不支持
    // 手机端：voice 列表常为空，跳过此检查（准入已由 isCurrentLanguageSpeakable 控制）
    if (!isMobileDevice && voices.length > 0 && !matchedVoice) {
      setSpeechPlayState('idle');
      return;
    }

    const speakNext = () => {
      if (speechRequestRef.current !== requestId) return;
      const idx = currentSegmentIndexRef.current;
      const segment = chapterSpeechSegments[idx];
      if (!segment) {
        activeUtteranceRef.current = null;
        setSpeechPlayState('idle');
        setCurrentSpeakingVerse(null);
        releaseWakeLock();
        return;
      }

      setCurrentSpeakingVerse(segment.verseNum);

      const utterance = new SpeechSynthesisUtterance(segment.text);
      utterance.lang = matchedVoice?.lang || targetLang;
      if (matchedVoice) {
        utterance.voice = matchedVoice;
      }
      utterance.onend = () => {
        if (speechRequestRef.current !== requestId) return;
        currentSegmentIndexRef.current = idx + 1;
        speakNext();
      };
      utterance.onerror = (e) => {
        if (speechRequestRef.current !== requestId) return;
        if (e.error === 'interrupted' || e.error === 'canceled') {
          setTimeout(() => speakNext(), 500);
          return;
        }
        activeUtteranceRef.current = null;
        setSpeechPlayState('idle');
        setCurrentSpeakingVerse(null);
        releaseWakeLock();
      };

      activeUtteranceRef.current = utterance;
      synth.speak(utterance);
      synth.resume();
    };

    setSpeechPlayState('playing');
    speakNext();
  }, [chapterSpeechSegments, isTtsSupported, loadSpeechVoices, primaryVersionMeta?.language, resolvedLanguage, releaseWakeLock, isMobileDevice]);

  const pauseSpeech = useCallback(() => {
    speechRequestRef.current += 1;
    const synth = window.speechSynthesis;
    synth.pause();
    synth.cancel();
    // 手机端 cancel 不一定清干净，延时再清一次
    setTimeout(() => { synth.cancel(); }, 100);
    releaseWakeLock();
    setSpeechPlayState('paused');
  }, [releaseWakeLock]);

  const toggleChapterSpeech = useCallback(async () => {
    if (!isTtsSupported || chapterSpeechSegments.length === 0) return;

    if (speechPlayState === 'playing') {
      pauseSpeech();
      return;
    }

    if (speechPlayState === 'paused') {
      startSpeechSequence(currentSegmentIndexRef.current);
      return;
    }

    // idle → 从头开始
    startSpeechSequence(0);
  }, [isTtsSupported, chapterSpeechSegments.length, speechPlayState, pauseSpeech, startSpeechSequence]);

  // 多版本经文数据：以主版本为结构模板，按 (Verse, Flag) 匹配各版本经文
  const multiVersionVerses = useMemo(() => {
    if (!selectedBookId || !selectedChapter || !bibles || !bibles[primaryVersionId]?.books) return [];
    const primaryBooks = bibles[primaryVersionId].books;
    const primaryBook = primaryBooks.find((b: any) => b._id === selectedBookId);
    const primaryBookIndex = primaryBook ? primaryBooks.findIndex((b: any) => b._id === primaryBook._id) : 0;
    const book_id = primaryBook?.book_id || primaryBooks[0]?.book_id || '';

    // 预建各版本 verse 索引: versionId -> "Verse|Flag" -> verse
    const versionIndex: Record<string, Record<string, any>> = {};
    const primaryBookRefs = primaryBook ? [primaryBook.book_id, primaryBook._id].filter(Boolean) : [book_id];
    for (const versionId of versionIds) {
      const vVersesAll = loadedBookVerses[versionId];
      if (!vVersesAll || vVersesAll.length === 0) continue;
      const vVerses = vVersesAll.filter((v: any) => v.Chapter * 1 === selectedChapter);
      const idx: Record<string, any> = {};
      for (const v of vVerses) {
        const flagKey = String(v.Flag || '0');
        idx[`${v.Verse}|${flagKey}`] = v;
        if (!idx[`${v.Verse}|0`]) idx[`${v.Verse}|0`] = v;
        if (!idx[`${v.Verse}|`]) idx[`${v.Verse}|`] = v;
      }
      versionIndex[versionId] = idx;
    }

    const primaryVersesAll = loadedBookVerses[primaryVersionId] || [];
    const primaryVerses = primaryVersesAll
      .filter((v: any) => primaryBookRefs.includes(v.Book) && v.Chapter * 1 === selectedChapter)
      .sort(compareVersesByPosition);

    return primaryVerses.map((pv: any) => {
      const verseNum = String(pv.Verse);
      const flag = String(pv.Flag || '0');
      const key = `${verseNum}|${flag}`;
      const flagSuffix = flag === '1' ? '上' : flag === '2' ? '下' : flag === '3' ? '中' : '';
      const isPrologue = verseNum === '0';
      const isMerged = String(pv.Text).trim() === 'a';

      const versionVerses: { versionId: string; verse: any; isAnnotationVersion: boolean }[] = [];
      const isPrimarySplitVerse = flag !== '0';
      const shouldRenderNonChineseOnThisBlock = !isPrimarySplitVerse || flag === '1';
      const shouldMergeRecoveryVerse = !isRecoveryVersionId(primaryVersionId) && flag === '0';

      for (const versionId of versionIds) {
        const isRecoveryVersion = isRecoveryVersionId(versionId);

        let v: any;
        if (versionId === primaryVersionId) {
          v = pv;
        } else if (isRecoveryVersion) {
          if (shouldMergeRecoveryVerse) {
            const recoveryParts = ['1', '3', '2']
              .map(partFlag => versionIndex[versionId]?.[`${verseNum}|${partFlag}`])
              .filter(Boolean);
            if (recoveryParts.length > 0) {
              const mergedText = recoveryParts.map((part: any) => part.Text).filter(Boolean).join('');
              v = {
                ...recoveryParts[0],
                Flag: '0',
                Text: mergedText,
                mergedRecoveryParts: recoveryParts,
              };
            } else {
              v = versionIndex[versionId]?.[`${verseNum}|0`] || versionIndex[versionId]?.[`${verseNum}|`];
            }
          } else {
            v = versionIndex[versionId]?.[key];
          }
        } else if (shouldRenderNonChineseOnThisBlock) {
          v = versionIndex[versionId]?.[`${verseNum}|0`] || versionIndex[versionId]?.[`${verseNum}|`];
        }

        if (v) {
          versionVerses.push({
            versionId,
            verse: v,
            isAnnotationVersion: versionId === annotationVersionId,
          });
        }
      }

      return {
        _key: `${pv.Book}-${pv.Chapter}-${verseNum}-${flag}`,
        verseNum,
        flag,
        flagSuffix,
        isPrologue,
        isMerged,
        versionVerses,
      };
    }).filter((mv: any) => mv.isMerged || mv.versionVerses.length > 0);
  }, [selectedBookId, selectedChapter, bibles, loadedBookVerses, versionIds, primaryVersionId, annotationVersionId]);

  const currentOutlines = useMemo(() => {
    return getOutlines(annotationVersionId, selectedBookId, selectedChapter);
  }, [selectedBookId, selectedChapter, bibles, versionIds]);

  // 全卷纲目（所有章节），按章节分组，用于 Modal 展示
  const allBookOutlines = useMemo(() => {
    if (!bibles || !bibles[annotationVersionId]?.outlines) return null;
    const refs = getBookRefsForVersion(annotationVersionId, selectedBookId);
    const all = bibles[annotationVersionId].outlines
      .filter((o: any) => refs.includes(o.book_index))
      .sort((a: any, b: any) => parseInt(a.chapter) - parseInt(b.chapter) || parseInt(a.section) - parseInt(b.section) || parseInt(a.level) - parseInt(b.level));
    const byChapter: Record<string, any[]> = {};
    all.forEach((o: any) => {
      const ch = String(o.chapter);
      if (!byChapter[ch]) byChapter[ch] = [];
      byChapter[ch].push(o);
    });
    return byChapter;
  }, [selectedBookId, bibles, versionIds]);

  useEffect(() => {
    if (loading) return;
    if (!multiVersionVerses.length) return;
    const id = window.setTimeout(() => {
      const container = mainContentRef.current;
      if (!container) return;
      const el = container.querySelector(`#verse-${CSS.escape(selectedVerse)}`) as HTMLElement | null;
      if (el) {
        const top = el.offsetTop - 56;
        container.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      } else {
        container.scrollTo({ top: 0, behavior: 'auto' });
      }
    }, 50);
    return () => window.clearTimeout(id);
  }, [loading, selectedBookId, selectedChapter, multiVersionVerses.length]);

  useEffect(() => {
    if (!isTtsSupported) return;

    const synth = window.speechSynthesis;
    const syncVoices = () => {
      const current = synth.getVoices();
      if (current.length > 0) {
        setSpeechVoices(current);
        setSpeechVoicesLoaded(true);
      }
    };

    syncVoices();
    synth.addEventListener('voiceschanged', syncVoices);

    // 手机端 voiceschanged 可能不触发，定期轮询
    const pollId = window.setInterval(() => {
      const current = synth.getVoices();
      if (current.length > 0) {
        setSpeechVoices(current);
        setSpeechVoicesLoaded(true);
        window.clearInterval(pollId);
      }
    }, 1000);

    return () => {
      synth.removeEventListener('voiceschanged', syncVoices);
      window.clearInterval(pollId);
    };
  }, [isTtsSupported]);

  useEffect(() => {
    if (!isTtsSupported) return;
    window.speechSynthesis.getVoices();
  }, [isTtsSupported]);

  useEffect(() => {
    stopChapterSpeech();
  }, [selectedBookId, selectedChapter, primaryVersionId, stopChapterSpeech]);

  useEffect(() => {
    if (!currentSpeakingVerse) return;
    const el = document.getElementById(`verse-${currentSpeakingVerse}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentSpeakingVerse]);

  useEffect(() => () => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, []);

  // 页面从后台恢复时重新探测 voices（手机端切后台回来 voice 列表可能被清空）
  useEffect(() => {
    if (!isTtsSupported) return;
    const handleVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const synth = window.speechSynthesis;
      const current = synth.getVoices();
      if (current.length > 0) {
        setSpeechVoices(current);
        setSpeechVoicesLoaded(true);
      } else {
        // voices 被清空了，重置探测状态让按钮先亮着
        setSpeechVoices([]);
        setSpeechVoicesLoaded(false);
        // 触发重新加载
        synth.getVoices();
      }
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, [isTtsSupported]);

  const getChapterCount = (bookId: string): number => {
    if (!bibles || !bibles[primaryVersionId]?.books) return 1;
    const book = books.find((b: any) => b._id === bookId);
    if (!book) return 1;

    // 优先使用已加载的书卷经文
    const verses = loadedBookVerses[primaryVersionId];
    if (verses && verses.length > 0) {
      const filtered = verses.filter((v: any) => [book.book_id, book._id].includes(v.Book));
      if (filtered.length > 0) {
        const lastVerse = filtered[filtered.length - 1];
        return lastVerse ? (lastVerse.Chapter || 1) : 1;
      }
    }
    // 当前书卷未加载时返回默认值（选中书卷后会触发懒加载）
    return 1;
  }

  // 滚动到指定书卷（在左侧列表中）
  const scrollToBook = (bookId: string) => {
    // 先通过 books 数组解析出真正的 _id（与 resolveBookAliases 保持一致）
    const num = parseInt(bookId, 10);
    const resolvedId = (!isNaN(num) && num > 0 && num <= books.length)
      ? (books[num - 1]?._id || bookId)
      : bookId;
    const el = document.getElementById(`book-${resolvedId}`);
    if (el) {
      // 找到左侧容器
      const container = el.closest('.book-list') as HTMLElement | null;
      if (container) {
        container.scrollTo({
          top: el.offsetTop - 8,
          behavior: 'smooth'
        });
      }
    }
  };

  const bookTopic = getBookTopic(annotationVersionId, selectedBookId);

  const seqToLetter = (seq: any): string => {
    const n = parseInt(String(seq), 10);
    if (!Number.isFinite(n) || n < 1) return String(seq);
    let s = '';
    let v = n;
    while (v > 0) {
      const r = (v - 1) % 26;
      s = String.fromCharCode(97 + r) + s;
      v = Math.floor((v - 1) / 26);
    }
    return s;
  };

  function toChineseNum(n: number): string {
    const digits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
    if (n < 10) return digits[n];
    if (n < 20) return n === 10 ? '十' : `十${digits[n - 10]}`;
    if (n < 100) {
      const t = Math.floor(n / 10);
      const u = n % 10;
      return `${digits[t]}十${u ? digits[u] : ''}`;
    }
    if (n < 1000) {
      const h = Math.floor(n / 100);
      const rest = n % 100;
      let s = `${digits[h]}百`;
      if (rest === 0) return s;
      if (rest < 10) return `${s}零${digits[rest]}`;
      if (rest < 20) return rest === 10 ? `${s}一十` : `${s}一十${digits[rest - 10]}`;
      const t = Math.floor(rest / 10);
      const u = rest % 10;
      return `${s}${digits[t]}十${u ? digits[u] : ''}`;
    }
    return String(n);
  }

  const renderHighlightedSearchText = (text: string, query: string) => {
    if (!query.trim()) return text;
    const parts = text.split(query);
    if (parts.length === 1) return text;
    return parts.flatMap((part, index) => (
      index === parts.length - 1
        ? [part]
        : [part, <mark key={`search-hit-${index}`} className="search-highlight">{query}</mark>]
    ));
  };

  const handleMainScroll = useCallback(() => {
    const container = mainContentRef.current;
    if (!container) return;
    const verseEls = container.querySelectorAll('[id^="verse-"]');
    const threshold = 80;
    let current: string | null = null;
    for (let i = 0; i < verseEls.length; i++) {
      const el = verseEls[i] as HTMLElement;
      if (el.offsetTop - container.scrollTop <= threshold) {
        current = el.id.replace('verse-', '');
      } else {
        break;
      }
    }
    if (current && current !== selectedVerse) {
      setSelectedVerse(current);
    }
  }, [selectedVerse]);

  const increaseFont = () => setFontSize(prev => Math.min(prev + 2, 24));
  const decreaseFont = () => setFontSize(prev => Math.max(prev - 2, 10));

  // ── Context menu handlers ──
  const contextMenuJustOpenedRef = useRef(false);

  const closeContextMenu = useCallback(() => {
    if (contextMenuJustOpenedRef.current) {
      contextMenuJustOpenedRef.current = false;
      return; // 忽略长按弹出后立即触发的 click（来自 touchend）
    }
    setContextMenu(null);
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const getVerseTextFromContainer = (el: HTMLElement): string => {
    const lines = el.querySelectorAll('.verse-line');
    const texts: string[] = [];
    lines.forEach((line) => {
      const num = line.querySelector('.verse-number');
      const numText = num ? num.textContent?.trim() || '' : '';
      const clone = line.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('.verse-number-hidden, .verse-number, .footnote-num').forEach(n => n.remove());
      const bodyText = clone.textContent?.trim() || '';
      const lineText = numText ? `${numText} ${bodyText}` : bodyText;
      if (lineText.trim()) texts.push(lineText.trim());
    });
    return texts.join('\n');
  };

  const openContextMenuAt = useCallback((e: React.MouseEvent | React.TouchEvent, container: HTMLElement, verseKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    const verseText = getVerseTextFromContainer(container);
    let x: number, y: number;
    if ('touches' in e) {
      const touch = (e as React.TouchEvent).touches[0] || (e as React.TouchEvent).changedTouches[0];
      x = touch.clientX;
      y = touch.clientY;
    } else {
      x = (e as React.MouseEvent).clientX;
      y = (e as React.MouseEvent).clientY;
    }
    // 标记菜单刚打开，阻止紧接而来的 touchend→click 误关菜单
    contextMenuJustOpenedRef.current = true;
    setTimeout(() => { contextMenuJustOpenedRef.current = false; }, 500);
    setContextMenu({ x, y, verseText, verseKey });
  }, []);

  const handleVerseContextMenu = useCallback((e: React.MouseEvent, mv: any) => {
    const verseKey = `${selectedBookId}-${selectedChapter}-${mv.verseNum}`;
    openContextMenuAt(e, e.currentTarget as HTMLElement, verseKey);
  }, [selectedBookId, selectedChapter, openContextMenuAt]);

  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const handleVerseTouchStart = useCallback((e: React.TouchEvent, mv: any) => {
    const container = e.currentTarget as HTMLElement;
    const verseKey = `${selectedBookId}-${selectedChapter}-${mv.verseNum}`;
    const touch = e.touches[0];
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
    longPressTimerRef.current = setTimeout(() => {
      touchStartPosRef.current = null;
      openContextMenuAt(e, container, verseKey);
      try { navigator.vibrate?.(10); } catch {}
    }, 600);
  }, [selectedBookId, selectedChapter, openContextMenuAt]);

  const handleVerseTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartPosRef.current = null;
  }, []);

  const handleVerseTouchMove = useCallback((e: React.TouchEvent) => {
    if (!longPressTimerRef.current || !touchStartPosRef.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStartPosRef.current.x;
    const dy = touch.clientY - touchStartPosRef.current.y;
    // 允许 10px 的移动容差，防止手指微颤取消长按
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      touchStartPosRef.current = null;
    }
  }, []);

  const handleCopyVerse = useCallback(async () => {
    if (contextMenu) {
      const verseNum = contextMenu.verseKey.split('-').pop() || '';
      const bookName = getBookName(primaryVersionId, selectedBookId);
      const reference = useChineseChapterNumerals
        ? `${bookName} ${toChineseNum(selectedChapter)}:${verseNum}`
        : `${bookName} ${selectedChapter}:${verseNum}`;
      // 去掉经文开头已经自带的节号（reference 里已经有了）
      const body = contextMenu.verseText.replace(/^\d+\s*/, '');
      const fullText = `${reference}  ${body}`;
      try {
        await navigator.clipboard.writeText(fullText);
      } catch {
        // fallback for older browsers
        const ta = document.createElement('textarea');
        ta.value = fullText;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      closeContextMenu();
    }
  }, [contextMenu, closeContextMenu, primaryVersionId, selectedBookId, selectedChapter, useChineseChapterNumerals]);

  const handleToggleHighlight = useCallback(() => {
    if (!contextMenu) return;
    setHighlightedVerses(prev => {
      const next = new Set(prev);
      if (next.has(contextMenu.verseKey)) {
        next.delete(contextMenu.verseKey);
      } else {
        next.add(contextMenu.verseKey);
      }
      return next;
    });
    closeContextMenu();
  }, [contextMenu, closeContextMenu]);

  const handleImageGen = useCallback(() => {
    if (!contextMenu) return;
    const vParam = versionIds.join(',');
    window.open(`https://lv.aliving.stream/${selectedBookId}/${selectedChapter}/${contextMenu.verseKey.split('-').pop()}?v=${vParam}`, '_blank');
    closeContextMenu();
  }, [contextMenu, selectedBookId, selectedChapter, versionIds, closeContextMenu]);

  // Save highlighted verses on change
  useEffect(() => {
    try {
      localStorage.setItem('bible-highlighted-verses', JSON.stringify([...highlightedVerses]));
    } catch {}
  }, [highlightedVerses]);

  // Close context menu on scroll or blur
  useEffect(() => {
    if (!contextMenu) return;
    const handleScroll = () => closeContextMenu();
    const handleBlur = () => closeContextMenu();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeContextMenu();
    };
    window.addEventListener('scroll', handleScroll, { capture: true });
    window.addEventListener('blur', handleBlur);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu, closeContextMenu]);

  useEffect(() => {
    if (!sidebarVisible) return;
    const id = window.setTimeout(() => {
      const bookEl = document.querySelector('.sidebar .book-list .book-item.selected') as HTMLElement | null;
      const bookContainer = document.querySelector('.sidebar .book-list') as HTMLElement | null;
      if (bookEl && bookContainer) {
        const top = bookEl.offsetTop - bookContainer.clientHeight / 2 + bookEl.clientHeight / 2;
        bookContainer.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      }
      const chapterEl = document.querySelector('.sidebar .chapter-list .chapter-item.selected') as HTMLElement | null;
      const chapterContainer = document.querySelector('.sidebar .chapter-list') as HTMLElement | null;
      if (chapterEl && chapterContainer) {
        const top = chapterEl.offsetTop - chapterContainer.clientHeight / 2 + chapterEl.clientHeight / 2;
        chapterContainer.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      }
    }, 30);
    return () => window.clearTimeout(id);
  }, [sidebarVisible, selectedBookId]);

  if (loading || !bibles || !bibles[primaryVersionId]) return <div className="center">{t.loading}</div>;
  if (error) return <div className="center" style={{ color: 'red' }}>{t.error}: {error}</div>;

  const books = bibles[primaryVersionId]?.books || [];
  
  
  return (
    <div className={`app ${theme}`} style={{ fontSize: `${fontSize}px` }}>
      {localeOverride && (
        <div style={{ position: 'fixed', top: 52, right: 12, zIndex: 80, padding: '4px 8px', borderRadius: 999, background: 'rgba(0,0,0,0.08)', fontSize: 12 }}>
          locale={localeOverride}
        </div>
      )}
      {sidebarVisible && (
        <>
          <div className="sidebar-mask" onClick={() => { setSidebarVisible(false); setSearchVisible(false); }} />
          <div className="sidebar">
            <div className="sidebar-header">
              <div className="sidebar-search-row">
                <SearchOutlined style={{ color: '#9a8a82', fontSize: 16, marginRight: 8, flexShrink: 0 }} />
                <input
                  ref={sidebarSearchInputRef}
                  className="sidebar-search-input"
                  placeholder={t.searchPlaceholder}
                  value={searchVisible ? searchQuery : ''}
                  onChange={(e) => {
                    if (!searchVisible) setSearchVisible(true);
                    handleSearchChange(e.target.value);
                  }}
                  onFocus={() => { if (!searchVisible) setSearchVisible(true); }}
                  onKeyDown={(e) => { if (e.key === 'Escape') exitSearchMode(); }}
                />
                {searchVisible && (
                  <button type="button" className="sidebar-search-cancel" onClick={exitSearchMode}>
                    {t.cancel}
                  </button>
                )}
              </div>
            </div>

            {searchVisible ? (
              <>
                <div className="sidebar-tabs">
                  <div className={`sidebar-tab ${searchTab === 'verses' ? 'active' : ''}`} onClick={() => { setSearchTab('verses'); setSearchBookFilter(null); }}>
                    {t.verses} ({searchVerseResults.length})
                  </div>
                  <div className={`sidebar-tab ${searchTab === 'footnotes' ? 'active' : ''}`} onClick={() => { setSearchTab('footnotes'); setSearchBookFilter(null); }}>
                    {t.footnotes} ({searchFootnoteResults.length})
                  </div>
                </div>
                {(() => {
                  const isVerse = searchTab === 'verses';
                  const allResults = isVerse ? searchVerseResults : searchFootnoteResults;
                  // 统计每卷匹配数
                  const bookCounts: Record<string, { name: string; count: number }> = {};
                  if (isVerse) {
                    for (const v of searchVerseResults) {
                      const bid = getPrimaryBookByVerseBook(v.Book)?._id || bookIdMap[v.Book] || v.Book;
                      if (!bookCounts[bid]) bookCounts[bid] = { name: getBookName(primaryVersionId, bid), count: 0 };
                      bookCounts[bid].count++;
                    }
                  } else {
                    for (const f of searchFootnoteResults) {
                      const bid = f.book_index;
                      if (!bookCounts[bid]) bookCounts[bid] = { name: getBookName(primaryVersionId, bid), count: 0 };
                      bookCounts[bid].count++;
                    }
                  }
                  const bookEntries = Object.entries(bookCounts).sort((a, b) => b[1].count - a[1].count);
                  const selectedBookLabel = searchBookFilter
                    ? `${bookCounts[searchBookFilter]?.name || getBookName(primaryVersionId, searchBookFilter)} ${bookCounts[searchBookFilter]?.count || 0}`
                    : `${t.allBooks} ${allResults.length}`;
                  // 过滤结果
                  const filtered: any[] = searchBookFilter
                    ? (isVerse
                        ? searchVerseResults.filter(v => (getPrimaryBookByVerseBook(v.Book)?._id || bookIdMap[v.Book] || v.Book) === searchBookFilter)
                        : searchFootnoteResults.filter(f => f.book_index === searchBookFilter))
                    : allResults;
                  const displayCount = isVerse ? searchVerseDisplay : searchFootnoteDisplay;

                  return (
                    <>
                      {allResults.length > 0 && (
                        <div className="search-book-filter-bar">
                          <div className="search-book-dropdown" ref={searchBookDropdownRef}>
                            <button
                              type="button"
                              className={`search-book-dropdown-trigger ${searchBookDropdownOpen ? 'open' : ''}`}
                              onClick={() => setSearchBookDropdownOpen(prev => !prev)}
                            >
                              <span className="search-book-dropdown-label">{selectedBookLabel}</span>
                              <span className="search-book-dropdown-arrow">▾</span>
                            </button>
                            {searchBookDropdownOpen && (
                              <div className="search-book-dropdown-menu">
                                <button
                                  type="button"
                                  className={`search-book-dropdown-item ${!searchBookFilter ? 'active' : ''}`}
                                  onClick={() => {
                                    setSearchBookFilter(null);
                                    setSearchBookDropdownOpen(false);
                                  }}
                                >
                                  <span>{t.allBooks}</span>
                                  <span className="count">{allResults.length}</span>
                                </button>
                                {bookEntries.map(([bid, { name, count }]) => (
                                  <button
                                    type="button"
                                    key={bid}
                                    className={`search-book-dropdown-item ${searchBookFilter === bid ? 'active' : ''}`}
                                    onClick={() => {
                                      setSearchBookFilter(bid);
                                      setSearchBookDropdownOpen(false);
                                    }}
                                  >
                                    <span>{name}</span>
                                    <span className="count">{count}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="sidebar-body search-body" ref={searchBodyRef}
                        onScroll={() => {
                          const el = searchBodyRef.current;
                          if (!el) return;
                          if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
                            if (isVerse) {
                              setSearchVerseDisplay(prev => Math.min(prev + 20, filtered.length));
                            } else {
                              setSearchFootnoteDisplay(prev => Math.min(prev + 20, filtered.length));
                            }
                          }
                        }}
                      >
                        {allResults.length === 0 ? (
                          <div className="search-empty">{searchQuery ? t.noSearchResult : t.searchHint}</div>
                        ) : filtered.length === 0 ? (
                          <div className="search-empty">{t.noBookResult}</div>
                        ) : isVerse ? (
                          filtered.slice(0, displayCount).map((v: any, i: number) => {
                            const bookName = getBookName(primaryVersionId, getPrimaryBookByVerseBook(v.Book)?._id || bookIdMap[v.Book] || v.Book);
                            const flag = String(v.Flag || '0');
                            const fs = flag === '1' ? '上' : flag === '2' ? '下' : flag === '3' ? '中' : '';
                            return (
                              <div key={v._id || i} className="search-result-item" onClick={() => jumpToVerseResult(v)}>
                                <span className="search-result-text">{renderHighlightedSearchText(v.Text, searchQuery)}</span>
                                <span className="search-result-ref">{bookName} {useChineseChapterNumerals ? toChineseNum(v.Chapter * 1) : v.Chapter} {t.chapterLabel}{v.Verse}{fs} {t.verseLabel}</span>
                              </div>
                            );
                          })
                        ) : (
                          filtered.slice(0, displayCount).map((f: any, i: number) => {
                            const bookName = getBookName(primaryVersionId, f.book_index);
                            return (
                              <div key={f._id || i} className="search-result-item"
                                onClick={() => {
                                  const bid = f.book_index;
                                  setSelectedBookId(bid);
                                  setSelectedChapter(parseInt(f.chapter));
                                  setSelectedVerse(String(f.section));
                                  setSidebarVisible(false);
                                  setSearchVisible(false);
                                  setTimeout(() => {
                                    nextModalId.current += 1;
                                    pushModal({ id: `fn-${nextModalId.current}`, bookId: bid, chapter: parseInt(f.chapter), footnotes: [{ seq: f.seq, note: f.note, verse: f.section, location: parseInt(f.location) || 0, flag: String(f.flag || '0') }] });
                                  }, 150);
                                }}
                              >
                                <span className="search-result-text">{renderHighlightedSearchText(f.note, searchQuery)}</span>
                                <span className="search-result-ref">{bookName} {useChineseChapterNumerals ? toChineseNum(parseInt(f.chapter)) : parseInt(f.chapter)} {t.chapterLabel}{f.section} {t.verseLabel} · {t.footnotes} {f.seq}</span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  );
                })()}
              </>
            ) : (
              <>
                <div className="sidebar-tabs">
                  <div className="sidebar-tab active">{t.toc}</div>
                </div>

                <div className="sidebar-body" ref={scrollRef}>
                  <div className="book-list">
                    {books.map((book: any) => (
                      <div
                        key={book._id}
                        id={`book-${book._id}`}
                        className={`book-item ${selectedBookId === book._id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedBookId(book._id);
                          setSelectedChapter(1);
                          setSelectedVerse('1');
                        }}
                      >
                        {book._id}. {primaryVersionId.indexOf('Chi') === 0 && book.name.length === 7
                          ? `${book.name.substr(0, 2)}...${book.name.substr(-3)}`
                          : book.name}
                      </div>
                    ))}
                  </div>

                  <div className="chapter-list">
                    {selectedBookId ? (
                      Array.from({ length: getChapterCount(selectedBookId) }, (_, i) => i + 1).map(ch => (
                        <div
                          key={ch}
                          onClick={() => {
                            setSelectedChapter(ch);
                            setSelectedVerse('1');
                            setSidebarVisible(false);
                            setTimeout(() => {
                              mainContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                            }, 100);
                          }}
                          className={`chapter-item ${ch === selectedChapter ? 'selected' : ''}`}
                        >
                          {t.titleChapterPrefix}{useChineseChapterNumerals ? toChineseNum(ch) : ch}{t.titleChapterSuffix}
                        </div>
                      ))
                    ) : (
                      <div className="chapter-empty">{t.chooseBook}</div>
                    )}
                  </div>
                </div>

                <div className="sidebar-footer">
                  <button
                    className={`testament-btn ${btnposition === 'old' ? 'active' : ''}`}
                    onClick={() => { setBtnposition('old'); scrollToBook('1'); }}
                  >
                    {t.oldTestament}
                  </button>
                  <button
                    className={`testament-btn ${btnposition === 'new' ? 'active' : ''}`}
                    onClick={() => { setBtnposition('new'); scrollToBook('40'); }}
                  >
                    {t.newTestament}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <div className="main-content" ref={mainContentRef} onScroll={handleMainScroll}>
        <div className="top-bar">
          <h4>{selectedBookId && selectedChapter ? `${getBookName(primaryVersionId, selectedBookId)} ${t.titleChapterPrefix}${useChineseChapterNumerals ? toChineseNum(selectedChapter) : selectedChapter}${t.titleChapterSuffix}` : ''}</h4>
        </div>

        <div className="content">
          {selectedChapter === 1 && (() => {
            const intros = getBookIntros(annotationVersionId, selectedBookId);
            if (intros.length === 0) return null;
            return (
              <div className="book-intro-card">
                <div className="book-intro-title">
                  {getBookName(primaryVersionId, selectedBookId)}
                </div>
                {intros.map((intro: any) => (
                  <div key={intro._id} className="book-intro-item">
                    <span className="book-intro-content">{intro.intro}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {selectedChapter === 1 && bookTopic && (
            <div className="book-topic">
              <strong>{t.topic}：</strong> {bookTopic}
            </div>
          )}

          <div className="verses">
            {(() => {
              const outlinesBySection: Record<string, any[]> = {};
              (currentOutlines || []).forEach((o: any) => {
                const k = String(o.section);
                if (!outlinesBySection[k]) outlinesBySection[k] = [];
                outlinesBySection[k].push(o);
              });
              return multiVersionVerses.map((mv: any, idx: number) => {
                const isPrologue = mv.isPrologue;
                const isMerged = mv.isMerged;
                const prev = idx > 0 ? multiVersionVerses[idx - 1] : null;
                const showOutlinesHere = !isMerged && (mv.flag === '0' || mv.flag === '1');
                const matchedOutlines = showOutlinesHere ? (outlinesBySection[String(mv.verseNum)] || []) : [];
                const hasMultipleVersions = mv.versionVerses.length > 1;

                return (
                  <React.Fragment key={mv._key}>
                    {matchedOutlines.map((o: any) => (
                      <div
                        key={o._id}
                        className={`outline-heading outline-level-${o.level || '1'}`}
                        onClick={(e) => { e.stopPropagation(); nextModalId.current += 1; pushModal({ id: `outline-${nextModalId.current}`, bookId: selectedBookId }); }}
                      >
                        {o.outline}
                      </div>
                    ))}
                    <div
                      className={`verse-container ${isPrologue ? 'verse-prologue' : ''} ${isMerged ? 'verse-merged' : ''} ${hasMultipleVersions ? 'multi-version-block' : ''} ${currentSpeakingVerse === mv.verseNum && mv.flag === '0' ? 'verse-speaking' : ''} ${!isPrologue && !isMerged && mv.flag === '0' && highlightedVerses.has(`${selectedBookId}-${selectedChapter}-${mv.verseNum}`) ? 'verse-highlighted' : ''}`}
                      id={`verse-${mv.verseNum}${mv.flagSuffix ? `-${mv.flag}` : ''}`}
                      onContextMenu={(e) => !isPrologue && !isMerged && mv.flag === '0' && handleVerseContextMenu(e, mv)}
                      onTouchStart={(e) => !isPrologue && !isMerged && mv.flag === '0' && handleVerseTouchStart(e, mv)}
                      onTouchEnd={handleVerseTouchEnd}
                      onTouchMove={handleVerseTouchMove}
                    >
                      <div className="verse-lines">
                        {isMerged && (
                          <div className="verse-line verse-merge-line">
                            <span className="verse-number">
                              {mv.verseNum}
                              {' '}
                            </span>
                            <span className="verse-merge-note">
                              ({prev ? `${prev.verseNum}、` : ''}{mv.verseNum}{t.mergedVerseNote})
                            </span>
                          </div>
                        )}
                        {mv.versionVerses.filter((vv: any) => !isMerged || vv.versionId !== primaryVersionId).map((vv: any, vi: number) => {
                            const verseFlag = String(vv.verse.Flag || '0');
                            const isFirst = !isMerged && vi === 0;
                            const colorIdx = versionIds.indexOf(vv.versionId);
                            const textColor = VERSION_COLORS[colorIdx] || 'inherit';
                            const isMergeText = String(vv.verse.Text).trim() === 'a';
                            return (
                              <div key={vv.versionId} className={`verse-line ${vv.isAnnotationVersion ? 'verse-line-annotated' : ''} ${isMergeText ? 'verse-line-merge-text' : ''}`}>
                                {isFirst && !isPrologue && (
                                  <span className="verse-number">
                                    {mv.verseNum}
                                    {mv.flagSuffix && <sup className="verse-half">{mv.flagSuffix}</sup>}
                                    {' '}
                                  </span>
                                )}
                                {!isFirst && !isPrologue && (
                                  <span className="verse-number verse-number-hidden">{mv.verseNum}{mv.flagSuffix ? <sup className="verse-half">{mv.flagSuffix}</sup> : ''} </span>
                                )}
                                {vv.isAnnotationVersion
                                  ? <span style={{ color: textColor !== 'inherit' ? textColor : undefined }}>{renderVerseText(vv.verse, selectedBookId, selectedChapter, verseFlag, `${mv._key}-${vv.verse._id}-`)}</span>
                                  : isMergeText
                                    ? <span className="verse-merge-note">({prev ? `${prev.verseNum}、` : ''}{mv.verseNum}{t.mergedVerseNote})</span>
                                    : <span style={{ color: textColor !== 'inherit' ? textColor : undefined }}>{vv.verse.Text}</span>
                                }
                              </div>
                            );
                          })}
                        </div>
                    </div>
                  </React.Fragment>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* 底部工具栏 */}
      <div className="bottom-toolbar">
        <button
          className="toolbar-btn"
          onClick={() => setSidebarVisible(!sidebarVisible)}
          title={t.toc}
        >
          <MenuOutlined />
        </button>
        <button
          className="toolbar-btn"
          onClick={decreaseFont}
          title="缩小字号"
        >
          A−
        </button>
        <button
          className="toolbar-btn"
          onClick={increaseFont}
          title="放大字号"
        >
          A+
        </button>
        <Popover
          trigger="click"
          placement="top"
          content={
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="small" onClick={() => setTheme('light')}>{t.themeLight}</Button>
              <Button size="small" onClick={() => setTheme('dark')}>{t.themeDark}</Button>
              <Button size="small" onClick={() => setTheme('classic')}>{t.themeClassic}</Button>
            </div>
          }
        >
          <button className="toolbar-btn" title={t.theme}>
            <BulbOutlined />
          </button>
        </Popover>
        <Popover
          trigger="click"
          placement="top"
          open={versionPickerOpen}
          onOpenChange={setVersionPickerOpen}
          content={
            <div className="version-picker-popover">
              <div className="version-picker-header">{t.versionPicker}（{versionIds.length}/5）</div>
              <div className="version-picker-list">
                {(() => {
                  const allVersions = sortedAvailableVersions;
                  // 按语言分组
                  const grouped: Record<string, typeof allVersions> = {};
                  allVersions.forEach(v => {
                    const langKey = v.languageName || v.language;
                    if (!grouped[langKey]) grouped[langKey] = [];
                    grouped[langKey].push(v);
                  });
                  return Object.entries(grouped).map(([langName, versions]) => (
                    <div key={langName} className="version-group">
                      <div className="version-group-title">{langName}</div>
                      {versions.map(v => {
                        const checked = versionIds.includes(v.id);
                        const disabled = !checked && versionIds.length >= 5;
                        const isOnlyOne = versionIds.length === 1 && checked;
                        return (
                          <label
                            key={v.id}
                            className={`version-item ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disabled || isOnlyOne}
                              onChange={() => {
                                if (checked) {
                                  if (versionIds.length <= 1) return;
                                  setVersionIds(prev => sortVersionIds(prev.filter(id => id !== v.id)));
                                } else {
                                  if (versionIds.length >= 5) return;
                                  setVersionIds(prev => sortVersionIds([...prev, v.id]));
                                }
                              }}
                            />
                            <span className="version-item-name">{v.name}</span>
                            <span className="version-item-id">{v.id}</span>
                          </label>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
              <div className="version-picker-footer">
                <a href="mailto:privacy@aliving.stream">{t.versionFeedback}</a>
              </div>
            </div>
          }
        >
          <button className={`toolbar-btn ${versionIds.length > 1 ? 'toolbar-btn-active' : ''}`} title={t.version}>
            <ReadOutlined />
            <span className="version-count">{versionIds.length > 1 ? versionIds.length : ''}</span>
          </button>
        </Popover>
        {isTtsSupported && (
          <button
            className={`toolbar-btn ${speechPlayState !== 'idle' ? 'toolbar-btn-active' : ''}`}
            title={isCurrentLanguageSpeakable ? (speechPlayState === 'playing' ? t.stopReading : t.readAloud) : t.readAloud}
            aria-label={isCurrentLanguageSpeakable ? (speechPlayState === 'playing' ? t.stopReading : t.readAloud) : t.readAloud}
            onClick={toggleChapterSpeech}
            disabled={speechPlayState === 'idle' && (!isCurrentLanguageSpeakable || chapterSpeechSegments.length === 0)}
          >
            {speechPlayState === 'playing' ? <PauseCircleOutlined /> : <SoundOutlined />}
          </button>
        )}
        <button className="toolbar-btn" title={t.search} onClick={openSearch}>
          <SearchOutlined />
        </button>
      </div>

      {/* Modal 堆栈 */}
      {modalStack.length > 0 && (() => {
        const top = modalStack[modalStack.length - 1];
        const isFootnote = !!top.footnotes;
        const isGroupedParen = !!top.groupedParenEntries?.length;
        const isVerseRef = !!top.verseRef;
        const isVerseRanges = !!top.verseRanges;
        const isAnnRef = !!top.annRef && !isVerseRanges;

        let headerText = '';
        if (isFootnote) {
          headerText = `${getBookName(primaryVersionId, top.bookId)} ${top.chapter || selectedChapter}:${top.footnotes![0].verse}：注${top.footnotes![0].seq}`;
        } else if (isGroupedParen) {
          headerText = (top.groupedParenLabel || '').replace(/^（/, '').replace(/）$/, '');
        } else if (isVerseRef) {
          headerText = `${getBookName(primaryVersionId, top.verseRef!.bookId)} ${useChineseChapterNumerals ? toChineseNum(top.verseRef!.chapter) : top.verseRef!.chapter} ${t.chapterLabel}${top.verseRef!.verse} ${t.verseLabel}`;
        } else if (isAnnRef) {
          headerText = top.annRef!.label || `${getBookName(primaryVersionId, top.annRef!.bookId)} ${useChineseChapterNumerals ? toChineseNum(top.annRef!.chapter) : top.annRef!.chapter} ${t.chapterLabel}${top.annRef!.verse} ${t.verseLabel} · ${t.footnotes} ${top.annRef!.seq}`;
        } else if (isVerseRanges) {
          headerText = (top.rangeLabel || '').replace(/^（/, '').replace(/）$/, '');
        } else {
          headerText = `${getBookName(primaryVersionId, top.bookId)} — 纲目`;
        }

        return (
          <div className="outline-modal-overlay" onClick={popModal}>
            <div className="outline-modal" onClick={(e) => e.stopPropagation()}>
              <div className="outline-modal-header">
                <h3>{headerText}</h3>
              </div>
              <div className="outline-modal-body">
                {isFootnote ? (
                  top.footnotes!.map((fn) => (
                    <div key={fn._id || `${fn.verse}-${fn.seq}-${fn.location}`} className="footnote-item">
                      <div className="footnote-item-note">{parseRefs(fn.note, top.bookId, top.chapter || selectedChapter, fn.verse, fn.flag, fn._id, fn.location)}</div>
                    </div>
                  ))
                ) : isGroupedParen ? (
                  <>
                    {top.groupedParenEntries!.map((entry, entryIndex) => {
                      const entryTitle = (
                        <div className="outline-chapter-title" style={{ cursor: 'default', marginTop: entryIndex > 0 ? 16 : 0 }}>
                          {entry.label}
                        </div>
                      );

                      if (entry.kind === 'verse') {
                        const verseBook = bibles[primaryVersionId]?.books?.find((b: any) => b._id === entry.bookId);
                        const verseBookId = verseBook?.book_id || '';
                        const currentVerses = getDisplayVersesForBook(primaryVersionId, entry.bookId);
                        const pendingBookLoad = !hasDisplayVersesForBook(primaryVersionId, entry.bookId) || isDisplayBookLoading(primaryVersionId, entry.bookId);
                        const refVerses = currentVerses.filter((v: any) =>
                          [verseBookId, entry.bookId].includes(v.Book) && v.Chapter * 1 === entry.chapter && String(v.Verse) === entry.verse
                        ).sort(compareVersesByPosition);

                        return (
                          <React.Fragment key={entry.id}>
                            {entryTitle}
                            {refVerses.length === 0 ? (
                              <div className="outline-modal-empty">{pendingBookLoad ? t.loading : t.noVerseFound}</div>
                            ) : refVerses.map((v: any) => {
                              const f = String(v.Flag || '0');
                              const fs = f === '1' ? '上' : f === '2' ? '下' : f === '3' ? '中' : '';
                              return (
                                <div key={v._id} className="verse-container verse-merged">
                                  <span className="verse-number">{v.Verse}{fs && <sup className="verse-half">{fs}</sup>} </span>
                                  {renderVerseText(v, entry.bookId, entry.chapter, f, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || '0'}-group-`)}
                                </div>
                              );
                            })}
                          </React.Fragment>
                        );
                      }

                      if (entry.kind === 'ann') {
                        const ar = entry.annRef;
                        const annFootnote = getAnnotationFootnote(annotationVersionId, ar.bookId, ar.chapter, ar.verse, ar.seq, ar.flag, ar.sourceFootnoteId, ar.sourceLocation, ar.endVerse);
                        const verseBook = bibles[primaryVersionId]?.books?.find((b: any) => b._id === ar.bookId);
                        const verseBookId = verseBook?.book_id || '';
                        const currentVerses = getDisplayVersesForBook(primaryVersionId, ar.bookId);
                        const pendingBookLoad = !hasDisplayVersesForBook(primaryVersionId, ar.bookId) || isDisplayBookLoading(primaryVersionId, ar.bookId);
                        const refVerses = currentVerses.filter((v: any) =>
                          [verseBookId, ar.bookId].includes(v.Book) && v.Chapter * 1 === ar.chapter && String(v.Verse) === ar.verse
                        ).sort(compareVersesByPosition);

                        const hasAnnotation = !!(annFootnote && hasRenderableFootnoteNote(annFootnote.note));
                        const annFlag = String(annFootnote?.flag || ar.flag || '0');

                        return (
                          <React.Fragment key={entry.id}>
                            {entryTitle}
                            {refVerses.length > 0 ? refVerses.map((v: any) => {
                              const f = String(v.Flag || '0');
                              const fs = f === '1' ? '上' : f === '2' ? '下' : f === '3' ? '中' : '';
                              return (
                                <div key={v._id} className="verse-container verse-merged">
                                  <span className="verse-number">{v.Verse}{fs && <sup className="verse-half">{fs}</sup>} </span>
                                  {renderVerseText(v, ar.bookId, ar.chapter, f, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || '0'}-group-ann-`)}
                                </div>
                              );
                            }) : (
                              <div className="outline-modal-empty">{pendingBookLoad ? t.loading : t.noVerseFound}</div>
                            )}

                            {hasAnnotation ? (
                              <div className="footnote-item">
                                <div className="footnote-item-note">{parseRefs(annFootnote!.note, ar.bookId, ar.chapter, ar.verse, annFlag, annFootnote!._id, parseInt(annFootnote!.location) || 0)}</div>
                              </div>
                            ) : (
                              <div className="outline-modal-empty">{pendingBookLoad ? t.loading : t.noFootnoteFound}</div>
                            )}
                          </React.Fragment>
                        );
                      }

                      const rangeAnnRef = entry.annRef;
                      const rangeAnnFootnote = rangeAnnRef
                        ? getAnnotationFootnote(
                          annotationVersionId,
                          rangeAnnRef.bookId,
                          rangeAnnRef.chapter,
                          rangeAnnRef.verse,
                          rangeAnnRef.seq,
                          rangeAnnRef.flag,
                          rangeAnnRef.sourceFootnoteId,
                          rangeAnnRef.sourceLocation,
                          rangeAnnRef.endVerse,
                        )
                        : null;
                      const hasRangeAnnotation = !!(rangeAnnFootnote && hasRenderableFootnoteNote(rangeAnnFootnote.note));
                      const rangeAnnFlag = String(rangeAnnFootnote?.flag || rangeAnnRef?.flag || '0');
                      const rangeBookIds = Array.from(new Set(entry.verseRanges.map(rg => rg.bookId || entry.bookId).filter(Boolean)));
                      const pendingRangeLoads = rangeBookIds.some(bookId => !hasDisplayVersesForBook(primaryVersionId, bookId) || isDisplayBookLoading(primaryVersionId, bookId));
                      const allVerses: { verse: any; bookId: string; chapter: number }[] = [];
                      for (const rg of entry.verseRanges) {
                        const bid = rg.bookId || entry.bookId;
                        const verseBook = bibles[primaryVersionId]?.books?.find((b: any) => b._id === bid);
                        const verseBookId = verseBook?.book_id || '';
                        for (let ch = rg.chapterStart; ch <= rg.chapterEnd; ch++) {
                          const currentVersesAll = getDisplayVersesForBook(primaryVersionId, bid);
                          const chVerses = currentVersesAll.filter((v: any) => {
                            if (![verseBookId, bid].includes(v.Book) || v.Chapter * 1 !== ch) return false;
                            const verseNum = parseInt(v.Verse);
                            return doesVerseOverlapRange(rg, ch, verseNum, v.Flag);
                          }).sort(compareVersesByPosition);
                          for (const cv of chVerses) allVerses.push({ verse: cv, bookId: bid, chapter: ch });
                        }
                      }

                      let lastChapter = -1;
                      let lastBookId = '';
                      return (
                        <React.Fragment key={entry.id}>
                          {entryTitle}
                          {allVerses.length > 0 ? allVerses.map(({ verse: v, bookId: vBookId, chapter: vChapter }) => {
                            const ch = v.Chapter * 1;
                            const showChapter = ch !== lastChapter || vBookId !== lastBookId;
                            lastChapter = ch;
                            lastBookId = vBookId;
                            const f = String(v.Flag || '0');
                            const fs = f === '1' ? '上' : f === '2' ? '下' : f === '3' ? '中' : '';
                            return (
                              <React.Fragment key={v._id}>
                                {showChapter && (
                                  <div className="outline-chapter-title" style={{ cursor: 'default', marginTop: (ch !== entry.verseRanges[0].chapterStart || vBookId !== (entry.verseRanges[0].bookId || entry.bookId)) ? 16 : 0 }}>
                                    {getBookName(primaryVersionId, vBookId)} {t.titleChapterPrefix}{useChineseChapterNumerals ? toChineseNum(ch) : ch}{t.titleChapterSuffix}
                                  </div>
                                )}
                                <div className="verse-container verse-merged">
                                  <span className="verse-number">{v.Verse}{fs && <sup className="verse-half">{fs}</sup>} </span>
                                  {renderVerseText(v, vBookId, vChapter, f, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || '0'}-group-range-`)}
                                </div>
                              </React.Fragment>
                            );
                          }) : (
                            <div className="outline-modal-empty">{pendingRangeLoads ? t.loading : t.noVerseFound}</div>
                          )}

                          {rangeAnnRef ? (hasRangeAnnotation ? (
                            <div className="footnote-item">
                              <div className="footnote-item-note">{parseRefs(rangeAnnFootnote!.note, rangeAnnRef.bookId, rangeAnnRef.chapter, rangeAnnRef.verse, rangeAnnFlag, rangeAnnFootnote!._id, parseInt(rangeAnnFootnote!.location) || 0)}</div>
                            </div>
                          ) : (
                            <div className="outline-modal-empty">{pendingRangeLoads ? t.loading : t.noFootnoteFound}</div>
                          )) : null}
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : isVerseRef ? (() => {
                  const vr = top.verseRef!;
                  const verseBook = bibles[primaryVersionId]?.books?.find((b: any) => b._id === vr.bookId);
                  const verseBookId = verseBook?.book_id || '';
                  const currentVerses = getDisplayVersesForBook(primaryVersionId, vr.bookId);
                  const pendingBookLoad = !hasDisplayVersesForBook(primaryVersionId, vr.bookId) || isDisplayBookLoading(primaryVersionId, vr.bookId);
                  const refVerses = currentVerses.filter((v: any) =>
                    [verseBookId, vr.bookId].includes(v.Book) && v.Chapter * 1 === vr.chapter && String(v.Verse) === vr.verse
                  ).sort(compareVersesByPosition);
                  if (refVerses.length === 0) {
                    return <div className="outline-modal-empty">{pendingBookLoad ? t.loading : t.noVerseFound}</div>;
                  }
                  return refVerses.map((v: any) => {
                    const f = String(v.Flag || '0');
                    const fs = f === '1' ? '上' : f === '2' ? '下' : f === '3' ? '中' : '';
                    return (
                      <div key={v._id} className="verse-container verse-merged">
                        <span className="verse-number">{v.Verse}{fs && <sup className="verse-half">{fs}</sup>} </span>
                        {renderVerseText(v, vr.bookId, vr.chapter, f, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || '0'}-`)}
                      </div>
                    );
                  });
                })() : isAnnRef ? (() => {
                  const ar = top.annRef!;
                  const annFootnote = getAnnotationFootnote(annotationVersionId, ar.bookId, ar.chapter, ar.verse, ar.seq, ar.flag, ar.sourceFootnoteId, ar.sourceLocation, ar.endVerse);
                  const verseBook = bibles[primaryVersionId]?.books?.find((b: any) => b._id === ar.bookId);
                  const verseBookId = verseBook?.book_id || '';
                  const currentVerses = getDisplayVersesForBook(primaryVersionId, ar.bookId);
                  const pendingBookLoad = !hasDisplayVersesForBook(primaryVersionId, ar.bookId) || isDisplayBookLoading(primaryVersionId, ar.bookId);
                  const refVerses = currentVerses.filter((v: any) =>
                    [verseBookId, ar.bookId].includes(v.Book) && v.Chapter * 1 === ar.chapter && String(v.Verse) === ar.verse
                  ).sort(compareVersesByPosition);

                  if (refVerses.length === 0 && (!annFootnote || !hasRenderableFootnoteNote(annFootnote.note))) {
                    return <div className="outline-modal-empty">{pendingBookLoad ? t.loading : t.noFootnoteFound}</div>;
                  }

                  const annFlag = String(annFootnote?.flag || ar.flag || '0');

                  return (
                    <>
                      {refVerses.length > 0 ? refVerses.map((v: any) => {
                        const f = String(v.Flag || '0');
                        const fs = f === '1' ? '上' : f === '2' ? '下' : f === '3' ? '中' : '';
                        return (
                          <div key={v._id} className="verse-container verse-merged">
                            <span className="verse-number">{v.Verse}{fs && <sup className="verse-half">{fs}</sup>} </span>
                            {renderVerseText(v, ar.bookId, ar.chapter, f, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || '0'}-ann-`)}
                          </div>
                        );
                      }) : (
                        <div className="outline-modal-empty">{pendingBookLoad ? t.loading : t.noVerseFound}</div>
                      )}

                      {annFootnote && hasRenderableFootnoteNote(annFootnote.note) ? (
                        <div className="footnote-item">
                          <div className="footnote-item-note">{parseRefs(annFootnote.note, ar.bookId, ar.chapter, ar.verse, annFlag, annFootnote._id, parseInt(annFootnote.location) || 0)}</div>
                        </div>
                      ) : (
                        <div className="outline-modal-empty">{pendingBookLoad ? t.loading : t.noFootnoteFound}</div>
                      )}
                    </>
                  );
                })() : isVerseRanges ? (() => {
                  const rangeAnnRef = top.annRef;
                  const rangeAnnFootnote = rangeAnnRef
                    ? getAnnotationFootnote(
                      annotationVersionId,
                      rangeAnnRef.bookId,
                      rangeAnnRef.chapter,
                      rangeAnnRef.verse,
                      rangeAnnRef.seq,
                      rangeAnnRef.flag,
                      rangeAnnRef.sourceFootnoteId,
                      rangeAnnRef.sourceLocation,
                      rangeAnnRef.endVerse,
                    )
                    : null;
                  const hasRangeAnnotation = !!(rangeAnnFootnote && hasRenderableFootnoteNote(rangeAnnFootnote.note));
                  const rangeAnnFlag = String(rangeAnnFootnote?.flag || rangeAnnRef?.flag || '0');
                  const rangeBookIds = Array.from(new Set(top.verseRanges!.map(rg => rg.bookId || top.bookId).filter(Boolean)));
                  const pendingRangeLoads = rangeBookIds.some(bookId => !hasDisplayVersesForBook(primaryVersionId, bookId) || isDisplayBookLoading(primaryVersionId, bookId));
                  const allVerses: { verse: any; bookId: string; chapter: number }[] = [];
                  for (const rg of top.verseRanges!) {
                    const bid = rg.bookId || top.bookId;
                    const verseBook = bibles[primaryVersionId]?.books?.find((b: any) => b._id === bid);
                    const verseBookId = verseBook?.book_id || '';
                    for (let ch = rg.chapterStart; ch <= rg.chapterEnd; ch++) {
                      const currentVersesAll = getDisplayVersesForBook(primaryVersionId, bid);
                      const chVerses = currentVersesAll.filter((v: any) => {
                        if (![verseBookId, bid].includes(v.Book) || v.Chapter * 1 !== ch) return false;
                        const verseNum = parseInt(v.Verse);
                        return doesVerseOverlapRange(rg, ch, verseNum, v.Flag);
                      }).sort(compareVersesByPosition);
                      for (const cv of chVerses) allVerses.push({ verse: cv, bookId: bid, chapter: ch });
                    }
                  }
                  if (allVerses.length === 0 && !hasRangeAnnotation) {
                    return <div className="outline-modal-empty">{pendingRangeLoads ? t.loading : t.noVerseFound}</div>;
                  }
                  let lastChapter = -1;
                  let lastBookId = '';
                  return (
                    <>
                      {allVerses.length > 0 ? allVerses.map(({ verse: v, bookId: vBookId, chapter: vChapter }) => {
                        const ch = v.Chapter * 1;
                        const showChapter = ch !== lastChapter || vBookId !== lastBookId;
                        lastChapter = ch;
                        lastBookId = vBookId;
                        const f = String(v.Flag || '0');
                        const fs = f === '1' ? '上' : f === '2' ? '下' : f === '3' ? '中' : '';
                        return (
                          <React.Fragment key={v._id}>
                            {showChapter && (
                              <div className="outline-chapter-title" style={{ cursor: 'default', marginTop: (ch !== top.verseRanges![0].chapterStart || vBookId !== (top.verseRanges![0].bookId || top.bookId)) ? 16 : 0 }}>
                                {getBookName(primaryVersionId, vBookId)} {t.titleChapterPrefix}{useChineseChapterNumerals ? toChineseNum(ch) : ch}{t.titleChapterSuffix}
                              </div>
                            )}
                            <div className="verse-container verse-merged">
                              <span className="verse-number">{v.Verse}{fs && <sup className="verse-half">{fs}</sup>} </span>
                              {renderVerseText(v, vBookId, vChapter, f, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || '0'}-`)}
                            </div>
                          </React.Fragment>
                        );
                      }) : (
                        <div className="outline-modal-empty">{pendingRangeLoads ? t.loading : t.noVerseFound}</div>
                      )}

                      {rangeAnnRef ? (hasRangeAnnotation ? (
                        <div className="footnote-item">
                          <div className="footnote-item-note">{parseRefs(rangeAnnFootnote!.note, rangeAnnRef.bookId, rangeAnnRef.chapter, rangeAnnRef.verse, rangeAnnFlag, rangeAnnFootnote!._id, parseInt(rangeAnnFootnote!.location) || 0)}</div>
                        </div>
                      ) : (
                        <div className="outline-modal-empty">{pendingRangeLoads ? t.loading : t.noFootnoteFound}</div>
                      )) : null}
                    </>
                  );
                })() : allBookOutlines && Object.keys(allBookOutlines).length > 0 ? (
                  Object.values(allBookOutlines).flat().map((o: any) => (
                    <div
                      key={o._id}
                      className={`outline-heading outline-level-${o.level || '1'} outline-clickable`}
                      onClick={() => { setSidebarVisible(false); setSelectedChapter(parseInt(o.chapter)); setSelectedVerse(String(o.section)); popModal(); }}
                    >
                      {o.outline}
                    </div>
                  ))
                ) : (
                  <div className="outline-modal-empty">{t.noOutline}</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* 经节右键/长按菜单 */}
      {contextMenu && (
        <>
          <div className="verse-context-menu-overlay" onClick={closeContextMenu} />
          <div
            ref={contextMenuRef}
            className="verse-context-menu"
            style={{
              left: Math.min(contextMenu.x, window.innerWidth - 180),
              top: Math.min(contextMenu.y, window.innerHeight - 160),
            }}
          >
            <div className="verse-context-menu-item" onClick={handleCopyVerse}>
              <span className="verse-context-menu-icon">
                <svg viewBox="64 64 896 896" width="16" height="16" fill="currentColor"><path d="M832 64H296c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8h496v688c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V96c0-17.7-14.3-32-32-32zM704 192H192c-17.7 0-32 14.3-32 32v530.7c0 8.5 3.4 16.6 9.4 22.6l173.3 173.3c2.2 2.2 4.7 4 7.4 5.5v1.9h4.2c3.5 1.3 7.2 2 11 2H704c17.7 0 32-14.3 32-32V224c0-17.7-14.3-32-32-32zM350 856.2L263.9 770H350v86.2zM664 888H414V746c0-22.1-17.9-40-40-40H232V264h432v624z"/></svg>
              </span>
              <span>{t.copyVerse}</span>
            </div>
            <div className="verse-context-menu-item" onClick={handleToggleHighlight}>
              <span className="verse-context-menu-icon">
                <svg viewBox="64 64 896 896" width="16" height="16" fill="currentColor"><path d="M957.6 507.4L603.2 158.2a7.9 7.9 0 00-11.2 0L353.3 393.4a8.03 8.03 0 00-.1 11.3l.1.1 40 39.4-117.2 115.3a8.03 8.03 0 00-.1 11.3l.1.1 39.5 38.9-189.1 187H72.1c-4.4 0-8.1 3.6-8.1 8V860c0 4.4 3.6 8 8 8h344.9c2.1 0 4.1-.8 5.6-2.3l212.2-210.1 42.1 41.4c3 3 7 4.7 11.2 4.7s8.3-1.7 11.2-4.7l258.4-252.9c3.2-3.1 3.2-8.2 0-11.3l.1-.1zM382 790H155.2V568.2L382 790zm363.1-357.1L544.2 631.4 416.7 506l200.9-198.9 127.5 125.8z"/></svg>
              </span>
              <span>{highlightedVerses.has(contextMenu.verseKey) ? t.unhighlightVerse : t.highlightVerse}</span>
            </div>
            <div className="verse-context-menu-item" onClick={handleImageGen}>
              <span className="verse-context-menu-icon">
                <svg viewBox="64 64 896 896" width="16" height="16" fill="currentColor"><path d="M928 160H96c-17.7 0-32 14.3-32 32v640c0 17.7 14.3 32 32 32h832c17.7 0 32-14.3 32-32V192c0-17.7-14.3-32-32-32zm-40 632H136v-39.9l138.5-164.3 150.1 178L658.1 489 888 761.6V792zm0-129.8L664.2 396.8c-3.2-3.8-9-3.8-12.2 0L424.6 666.4l-144-170.7c-3.2-3.8-9-3.8-12.2 0L136 652.7V232h752v430.2zM304 456a88 88 0 100-176 88 88 0 000 176zm0-116c15.5 0 28 12.5 28 28s-12.5 28-28 28-28-12.5-28-28 12.5-28 28-28z"/></svg>
              </span>
              <span>{t.imageGen}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default App;